import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { themeColor } from '../utils/themeColor';
import ReactDOM from 'react-dom';
import { resolveStrategyDisplayName } from './dashboard/resolveStrategyDisplayName';
import { API_BASE_URL } from '../config';
import { IconInfo, IconCheck, IconCopy } from './Icons';
import { DEFAULT_ACCOUNT_SIZE, getThresholdForAccountSize } from '../utils/accountSizes';
import { trackEvent } from '../utils/analytics';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import usePersistentTab from '../hooks/usePersistentTab';
import { getNasdaqRollWeekInfoForDay, isNasdaqFuturesContractType } from '../utils/cmeNasdaqFuturesRoll';
import { loadUsHighImpactEvents } from '../utils/usHighImpactCalendar';
import StrategyDetailsView from './StrategyDetailsView';
import {
  DEFAULT_DISPLAY_FILTER,
  isDisplayFilterActive,
  dayPassesDisplayFilter,
  uniqueMonthKeysFromStrategyRows,
  RED_FOLDER_FILTER_ENABLED,
} from '../utils/strategyDisplayFilter';
import { computeStrategySheetMetrics } from '../utils/strategySheetMetrics';
import { sortStrategies } from '../utils/strategySort';
import { PnlContractsInfoButton, DEFAULT_PNL_SCALING_PARAGRAPHS } from './PnlScalingInfoModal';
import './PortfolioSimulator.css';
import { getDataSourceNote, getEntityDisplayName } from '../utils/activeEntity';

// Non-strategy sheet names to filter from results
const NON_STRATEGY_SHEETS = new Set([
  'current results',
  'current results (hidden)',
  'past results',
  'past results (hidden)',
  'sheet2',
  'results',
  'results updated',
  'summary',
  'sample strategy (demo)',
]);
const DATE_SHEET_PATTERN = /^\d{2}\.\d{4}$/; // MM.YYYY format

// Common drawdown defaults per account size
const DRAWDOWN_DEFAULTS = {
  25000: 1500,
  50000: 2500,
  100000: 3000,
  150000: 4500,
  250000: 6500,
  300000: 7500,
};

function normalizeConsistencyPercent(value) {
  const parsed = Number(value);
  return [20, 30, 40, 50, 55, 60].includes(parsed) ? parsed : 50;
}

// Parse date from row label like "Fri (04.21.2025)" or "04.21.2025"
function parseDateFromRowLabel(label) {
  if (!label) return null;
  
  // Extract date pattern MM.DD.YYYY from various formats
  const dateMatch = label.match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
  if (dateMatch) {
    const [, mm, dd, yyyy] = dateMatch;
    const month = parseInt(mm, 10);
    const day = parseInt(dd, 10);
    const year = parseInt(yyyy, 10);
    
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year > 1900) {
      return new Date(year, month - 1, day);
    }
  }
  return null;
}

// Get date range based on filter selection
function getDateRange(filter, customStart, customEnd) {
  const now = new Date();
  let startDate = null;
  let endDate = null;

  switch (filter) {
    case 'all-time':
      return { startDate: null, endDate: null };
    
    case 'last-30-days':
      startDate = new Date(now);
      startDate.setDate(startDate.getDate() - 30);
      endDate = now;
      break;
    
    case 'last-3-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 3);
      endDate = now;
      break;
    
    case 'last-6-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 6);
      endDate = now;
      break;
    
    case 'last-12-months':
      startDate = new Date(now);
      startDate.setMonth(startDate.getMonth() - 12);
      endDate = now;
      break;
    
    case '2024':
      startDate = new Date(2024, 0, 1);
      endDate = new Date(2024, 11, 31);
      break;
    
    case '2025':
      startDate = new Date(2025, 0, 1);
      endDate = new Date(2025, 11, 31);
      break;
    
    case 'custom':
      if (customStart) {
        startDate = new Date(customStart);
      }
      if (customEnd) {
        endDate = new Date(customEnd);
      }
      break;
    
    default:
      return { startDate: null, endDate: null };
  }

  return { startDate, endDate };
}

// Filter rows by date range
function filterRowsByDateRange(rows, startDate, endDate) {
  if (!startDate && !endDate) {
    return rows; // No filter applied
  }

  // Keep first 2 rows (headers)
  if (rows.length <= 2) return rows;
  
  const headers = rows.slice(0, 2);
  const dataRows = rows.slice(2);

  const filteredDataRows = dataRows.filter(row => {
    // Date is in the first column (row[0])
    const dateLabel = Array.isArray(row) ? row[0] : (row.date || row.label || row.week);
    const rowDate = parseDateFromRowLabel(dateLabel);
    
    if (!rowDate) return true; // Keep rows without parseable dates (like totals)
    
    if (startDate && rowDate < startDate) return false;
    if (endDate && rowDate > endDate) return false;
    
    return true;
  });

  return [...headers, ...filteredDataRows];
}

function parseNumericCell(value) {
  if (value === undefined || value === null || value === '') return null;
  const cleaned = String(value).replace(/[$,]/g, '').trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * Merge selected strategies' sheet rows by summing Mon–Fri cells for each week (same Monday key).
 * Values stay in the same units as the source sheets (StrategyDetailsView applies contract sizing).
 */
function combinePortfolioRawRows(strategyNames, rawDataMap, scaleFor = () => 1) {
  const names = [...strategyNames];
  if (names.length === 0) return [];
  let template = null;
  for (const n of names) {
    const r = rawDataMap[n];
    if (r && r.length >= 3) {
      template = r;
      break;
    }
  }
  if (!template) return [];
  const header0 = Array.isArray(template[0]) ? [...template[0]] : [];
  const header1 = Array.isArray(template[1]) ? [...template[1]] : [];
  const headerRow = header1;
  const idxMonday = headerRow.findIndex((c) => String(c || '').toLowerCase() === 'monday');
  const idxTuesday = headerRow.findIndex((c) => String(c || '').toLowerCase() === 'tuesday');
  const idxWednesday = headerRow.findIndex((c) => String(c || '').toLowerCase() === 'wednesday');
  const idxThursday = headerRow.findIndex((c) => String(c || '').toLowerCase() === 'thursday');
  const idxFriday = headerRow.findIndex((c) => String(c || '').toLowerCase() === 'friday');
  const mondayCol = idxMonday >= 0 ? idxMonday : 2;
  const tuesdayCol = idxTuesday >= 0 ? idxTuesday : 3;
  const wednesdayCol = idxWednesday >= 0 ? idxWednesday : 4;
  const thursdayCol = idxThursday >= 0 ? idxThursday : 5;
  const fridayCol = idxFriday >= 0 ? idxFriday : 6;
  const dayCols = [mondayCol, tuesdayCol, wednesdayCol, thursdayCol, fridayCol];
  const rowLen = Math.max(headerRow.length, fridayCol + 1, 8);

  const byMonday = new Map();
  names.forEach((name) => {
    const rows = rawDataMap[name];
    if (!rows || rows.length < 3) return;
    rows.slice(2).forEach((row) => {
      const dateStr = row[0];
      if (!dateStr) return;
      const match = String(dateStr).match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
      if (!match) return;
      let acc = byMonday.get(dateStr);
      if (!acc) {
        acc = new Array(rowLen).fill('');
        acc[0] = dateStr;
        for (let i = 1; i < rowLen; i += 1) acc[i] = 0;
        byMonday.set(dateStr, acc);
      }
      const scale = scaleFor(name);
      dayCols.forEach((col) => {
        const v = parseNumericCell(row[col]);
        acc[col] = (Number(acc[col]) || 0) + (v || 0) * scale;
      });
    });
  });

  const sortedKeys = Array.from(byMonday.keys()).sort((a, b) => {
    const da = parseDateFromRowLabel(a);
    const db = parseDateFromRowLabel(b);
    if (!da || !db) return 0;
    return db - da;
  });
  const dataRows = sortedKeys.map((k) => byMonday.get(k));
  return [header0, header1, ...dataRows];
}

/** Sheet units → dollars for a contract type: MNQ is a tenth of NQ. */
function baseMultiplier(type) {
  return type === 'MNQ' ? 0.1 : 1.0;
}

// Calculate comprehensive statistics by day of the week from strategy sheet data (client-side version)
function calculateStrategyStatisticsByDay(data, contractMultiplier = 1.0) {
  if (!data || data.length < 3) {
    const emptyStats = {
      pf: 0, wins: 0, losses: 0, winRate: 0, avgWin: 0, avgLoss: 0, totalTrades: 0, netPL: 0
    };
    return {
      Monday: { ...emptyStats },
      Tuesday: { ...emptyStats },
      Wednesday: { ...emptyStats },
      Thursday: { ...emptyStats },
      Friday: { ...emptyStats },
      Overall: { ...emptyStats }
    };
  }

  // Skip header rows (rows 1-2), start from row 3 (index 2)
  const rows = data.slice(2);
  
  // Columns C-G are Monday-Friday (indices 2-6)
  const dayColumns = {
    Monday: 2,
    Tuesday: 3,
    Wednesday: 4,
    Thursday: 5,
    Friday: 6
  };

  const dayStats = {
    Monday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
    Tuesday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
    Wednesday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
    Thursday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
    Friday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
    Overall: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] }
  };

  // Helper to parse numeric value
  const parseNumericValue = (cellValue) => {
    if (cellValue === undefined || cellValue === null || cellValue === '') return null;
    const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  // Process each row
  rows.forEach((row) => {
    if (!row || row.length < 7) return; // Need at least columns A-G
    
    // Process each day
    Object.entries(dayColumns).forEach(([dayName, colIndex]) => {
      const value = parseNumericValue(row[colIndex]);
      if (value !== null && value !== 0) {
        // Apply contract multiplier (MNQ = 0.1, NQ = 1.0)
        const adjustedValue = value * contractMultiplier;
        
        dayStats[dayName].totalDays++;
        dayStats.Overall.totalDays++;
        
        if (adjustedValue > 0) {
          dayStats[dayName].wins += adjustedValue;
          dayStats[dayName].winDays++;
          dayStats[dayName].winValues.push(adjustedValue);
          dayStats.Overall.wins += adjustedValue;
          dayStats.Overall.winDays++;
          dayStats.Overall.winValues.push(adjustedValue);
        } else {
          const absValue = Math.abs(adjustedValue);
          dayStats[dayName].losses += absValue;
          dayStats[dayName].lossDays++;
          dayStats[dayName].lossValues.push(absValue);
          dayStats.Overall.losses += absValue;
          dayStats.Overall.lossDays++;
          dayStats.Overall.lossValues.push(absValue);
        }
      }
    });
  });

  // Calculate all metrics for each day
  const result = {};
  Object.keys(dayStats).forEach((dayName) => {
    const stats = dayStats[dayName];
    
    // Profit Factor
    const pf = stats.losses > 0 
      ? stats.wins / stats.losses 
      : (stats.wins > 0 ? Infinity : 0);
    
    // Win Rate (percentage of winning days)
    const winRate = stats.totalDays > 0 
      ? (stats.winDays / stats.totalDays) * 100 
      : 0;
    
    // Average Win
    const avgWin = stats.winValues.length > 0
      ? stats.wins / stats.winValues.length
      : 0;
    
    // Average Loss
    const avgLoss = stats.lossValues.length > 0
      ? stats.losses / stats.lossValues.length
      : 0;
    
    // Net P&L
    const netPL = stats.wins - stats.losses;
    
    result[dayName] = {
      pf: isFinite(pf) ? Math.round(pf * 100) / 100 : Infinity,
      winRate: Math.round(winRate * 10) / 10, // 1 decimal
      avgWin: Math.round(avgWin * 100) / 100,
      avgLoss: Math.round(avgLoss * 100) / 100,
      totalTrades: stats.totalDays,
      netPL: Math.round(netPL * 100) / 100,
      wins: Math.round(stats.wins * 100) / 100,
      losses: Math.round(stats.losses * 100) / 100
    };
  });

  return result;
}

// Calculate blowout risk probability by day with detailed metrics
function calculateBlowoutRiskByDay(data, accountSize, contractSize, maxDrawdown, contractMultiplier = 1.0) {
  if (!data || data.length < 3 || !maxDrawdown || maxDrawdown <= 0) {
    const emptyRisk = { probability: 0, breaches: 0, totalDays: 0 };
    return {
      Monday: { ...emptyRisk },
      Tuesday: { ...emptyRisk },
      Wednesday: { ...emptyRisk },
      Thursday: { ...emptyRisk },
      Friday: { ...emptyRisk },
      Overall: { ...emptyRisk },
      metrics: {
        highestLoss: 0,
        averageLoss: 0,
        averageProfit: 0,
        maxProfit: 0,
        perContractLoss: 0,
        riskScore: 0,
        buffer: maxDrawdown,
        totalDays: 0,
        winningDays: 0,
        losingDays: 0
      }
    };
  }

  const rows = data.slice(2);
  const dayColumns = {
    Monday: 2,
    Tuesday: 3,
    Wednesday: 4,
    Thursday: 5,
    Friday: 6
  };

  const dayStats = {
    Monday: { breaches: 0, totalDays: 0 },
    Tuesday: { breaches: 0, totalDays: 0 },
    Wednesday: { breaches: 0, totalDays: 0 },
    Thursday: { breaches: 0, totalDays: 0 },
    Friday: { breaches: 0, totalDays: 0 },
    Overall: { breaches: 0, totalDays: 0 }
  };

  const parseNumericValue = (cellValue) => {
    if (cellValue === undefined || cellValue === null || cellValue === '') return null;
    const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  // Collect all losses and profits for detailed metrics
  const allLosses = [];
  const allProfits = [];
  let totalDays = 0;
  let winningDays = 0;
  let losingDays = 0;

  rows.forEach((row) => {
    if (!row || row.length < 7) return;
    
    Object.entries(dayColumns).forEach(([dayName, colIndex]) => {
      const value = parseNumericValue(row[colIndex]);
      if (value !== null && value !== 0) {
        dayStats[dayName].totalDays++;
        dayStats.Overall.totalDays++;
        totalDays++;
        
        const adjustedValue = value * contractMultiplier;
        const scaledValue = adjustedValue * contractSize;
        
        // Check if loss (negative value) exceeds max drawdown when scaled by contract size and multiplier
        if (value < 0) {
          const adjustedLoss = Math.abs(adjustedValue);
          const scaledLoss = adjustedLoss * contractSize;
          allLosses.push(scaledLoss);
          losingDays++;
          
          if (scaledLoss > maxDrawdown) {
            dayStats[dayName].breaches++;
            dayStats.Overall.breaches++;
          }
        } else if (value > 0) {
          allProfits.push(scaledValue);
          winningDays++;
        }
      }
    });
  });

  // Calculate detailed metrics
  const worstLossPerContract = allLosses.length > 0 ? Math.max(...allLosses.map(l => l / contractSize)) : 0;
  const highestLoss = allLosses.length > 0 ? Math.max(...allLosses) : 0;
  const averageLoss = allLosses.length > 0 ? allLosses.reduce((a, b) => a + b, 0) / allLosses.length : 0;
  const maxProfit = allProfits.length > 0 ? Math.max(...allProfits) : 0;
  const averageProfit = allProfits.length > 0 ? allProfits.reduce((a, b) => a + b, 0) / allProfits.length : 0;
  
  // Calculate buffer
  const buffer = maxDrawdown - highestLoss;
  
  // Calculate risk score (similar to server logic)
  const worstLossPctOfAccount = accountSize > 0 ? (highestLoss / accountSize) : 0;
  const drawdownUsagePct = maxDrawdown > 0 ? Math.min(highestLoss / maxDrawdown, 1.0) : worstLossPctOfAccount;
  let riskScore = 0;
  riskScore += Math.min(worstLossPctOfAccount * 50, 50);
  riskScore += Math.min(drawdownUsagePct * 50, 50);
  riskScore = Math.min(Math.round(riskScore), 100);

  const result = {};
  Object.keys(dayStats).forEach((dayName) => {
    const stats = dayStats[dayName];
    const probability = stats.totalDays > 0 
      ? (stats.breaches / stats.totalDays) * 100 
      : 0;
    
    result[dayName] = {
      probability: Math.round(probability * 10) / 10,
      breaches: stats.breaches,
      totalDays: stats.totalDays
    };
  });

  // Add detailed metrics to result
  result.metrics = {
    highestLoss: Math.round(highestLoss * 100) / 100,
    averageLoss: Math.round(averageLoss * 100) / 100,
    averageProfit: Math.round(averageProfit * 100) / 100,
    maxProfit: Math.round(maxProfit * 100) / 100,
    perContractLoss: Math.round(worstLossPerContract * 100) / 100,
    riskScore,
    buffer: Math.round(buffer * 100) / 100,
    totalDays,
    winningDays,
    losingDays
  };

  return result;
}

// Calculate % Rule probability by day
function calculateThirtyPercentRuleByDay(data, accountSize, contractSize, startOfDayProfit, safetyNet, consistencyPercent = 50, contractMultiplier = 1.0) {
  if (!data || data.length < 3) {
    const emptyRisk = { probability: 0, breaches: 0, totalDays: 0, maeLimit: 0 };
    return {
      Monday: { ...emptyRisk },
      Tuesday: { ...emptyRisk },
      Wednesday: { ...emptyRisk },
      Thursday: { ...emptyRisk },
      Friday: { ...emptyRisk },
      Overall: { ...emptyRisk }
    };
  }

  // Calculate consistency limit (same logic as server)
  const profit = Number(startOfDayProfit) || 0;
  const net = Number(safetyNet) || 0;
  
  // If both are 0, return null-like result (all zeros) - user needs to set values
  if (!profit && !net) {
    const emptyRisk = { probability: 0, breaches: 0, totalDays: 0, maeLimit: 0 };
    return {
      Monday: { ...emptyRisk },
      Tuesday: { ...emptyRisk },
      Wednesday: { ...emptyRisk },
      Thursday: { ...emptyRisk },
      Friday: { ...emptyRisk },
      Overall: { ...emptyRisk }
    };
  }
  
  let baseAmount;
  if (profit <= net && net > 0) {
    baseAmount = net;
  } else {
    baseAmount = profit || net;
  }
  const limitPercent = normalizeConsistencyPercent(consistencyPercent) / 100;
  const maeLimit = baseAmount * limitPercent;

  if (maeLimit <= 0) {
    const emptyRisk = { probability: 0, breaches: 0, totalDays: 0, maeLimit: 0 };
    return {
      Monday: { ...emptyRisk },
      Tuesday: { ...emptyRisk },
      Wednesday: { ...emptyRisk },
      Thursday: { ...emptyRisk },
      Friday: { ...emptyRisk },
      Overall: { ...emptyRisk }
    };
  }

  const rows = data.slice(2);
  const dayColumns = {
    Monday: 2,
    Tuesday: 3,
    Wednesday: 4,
    Thursday: 5,
    Friday: 6
  };

  const dayStats = {
    Monday: { breaches: 0, totalDays: 0 },
    Tuesday: { breaches: 0, totalDays: 0 },
    Wednesday: { breaches: 0, totalDays: 0 },
    Thursday: { breaches: 0, totalDays: 0 },
    Friday: { breaches: 0, totalDays: 0 },
    Overall: { breaches: 0, totalDays: 0 }
  };

  const parseNumericValue = (cellValue) => {
    if (cellValue === undefined || cellValue === null || cellValue === '') return null;
    const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  rows.forEach((row) => {
    if (!row || row.length < 7) return;
    
    Object.entries(dayColumns).forEach(([dayName, colIndex]) => {
      const value = parseNumericValue(row[colIndex]);
      if (value !== null && value !== 0) {
        dayStats[dayName].totalDays++;
        dayStats.Overall.totalDays++;
        
        // Check if loss (negative value) exceeds consistency limit when scaled by contract size and multiplier
        if (value < 0) {
          const adjustedValue = Math.abs(value) * contractMultiplier;
          const scaledLoss = adjustedValue * contractSize;
          if (scaledLoss > maeLimit) {
            dayStats[dayName].breaches++;
            dayStats.Overall.breaches++;
          }
        }
      }
    });
  });

  const result = {};
  Object.keys(dayStats).forEach((dayName) => {
    const stats = dayStats[dayName];
    const probability = stats.totalDays > 0 
      ? (stats.breaches / stats.totalDays) * 100 
      : 0;
    
    result[dayName] = {
      probability: Math.round(probability * 10) / 10,
      breaches: stats.breaches,
      totalDays: stats.totalDays,
      maeLimit: maeLimit
    };
  });

  return result;
}

// Calculate windfall probability by day (% Consistency Rule)
function calculateWindfallProbabilityByDay(data, contractSize, profitSinceLastPayout = 2600, consistencyPercent = 50, contractMultiplier = 1.0) {
  const normalizedConsistencyPercent = normalizeConsistencyPercent(consistencyPercent);
  const consistencyRatio = normalizedConsistencyPercent / 100;
  if (!data || data.length < 3) {
    const emptyRisk = { probability: 0, breaches: 0, totalDays: 0, threshold: 2600 * consistencyRatio };
    return {
      Monday: { ...emptyRisk },
      Tuesday: { ...emptyRisk },
      Wednesday: { ...emptyRisk },
      Thursday: { ...emptyRisk },
      Friday: { ...emptyRisk },
      Overall: { ...emptyRisk }
    };
  }

  // Windfall threshold: configurable % of payout reference
  const LARGEST_SAFE_DAY = 2600 * consistencyRatio;
  const threshold = profitSinceLastPayout > 0 ? profitSinceLastPayout * consistencyRatio : LARGEST_SAFE_DAY;

  const rows = data.slice(2);
  const dayColumns = {
    Monday: 2,
    Tuesday: 3,
    Wednesday: 4,
    Thursday: 5,
    Friday: 6
  };

  const dayStats = {
    Monday: { breaches: 0, totalDays: 0 },
    Tuesday: { breaches: 0, totalDays: 0 },
    Wednesday: { breaches: 0, totalDays: 0 },
    Thursday: { breaches: 0, totalDays: 0 },
    Friday: { breaches: 0, totalDays: 0 },
    Overall: { breaches: 0, totalDays: 0 }
  };

  const parseNumericValue = (cellValue) => {
    if (cellValue === undefined || cellValue === null || cellValue === '') return null;
    const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  rows.forEach((row) => {
    if (!row || row.length < 7) return;
    
    Object.entries(dayColumns).forEach(([dayName, colIndex]) => {
      const value = parseNumericValue(row[colIndex]);
      if (value !== null && value !== 0) {
        dayStats[dayName].totalDays++;
        dayStats.Overall.totalDays++;
        
        // Check if profit (positive value) exceeds windfall threshold when scaled by contract size and multiplier
        if (value > 0) {
          const adjustedValue = value * contractMultiplier;
          const scaledProfit = adjustedValue * contractSize;
          if (scaledProfit > threshold) {
            dayStats[dayName].breaches++;
            dayStats.Overall.breaches++;
          }
        }
      }
    });
  });

  const result = {};
  Object.keys(dayStats).forEach((dayName) => {
    const stats = dayStats[dayName];
    const probability = stats.totalDays > 0 
      ? (stats.breaches / stats.totalDays) * 100 
      : 0;
    
    result[dayName] = {
      probability: Math.round(probability * 10) / 10,
      breaches: stats.breaches,
      totalDays: stats.totalDays,
      threshold: threshold
    };
  });

  return result;
}

// Helper function to calculate worst loss and highest profit from historical data
function calculateHistoricalExtremes(data, contractSize, contractMultiplier = 1.0) {
  if (!data || data.length < 3) {
    return { worstLoss: 0, highestProfit: 0 };
  }

  const rows = data.slice(2);
  const dayColumns = [2, 3, 4, 5, 6]; // Mon-Fri columns
  
  let worstLoss = 0;
  let highestProfit = 0;

  const parseNumericValue = (cellValue) => {
    if (cellValue === undefined || cellValue === null || cellValue === '') return null;
    const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  rows.forEach((row) => {
    if (!row || row.length < 7) return;
    
    dayColumns.forEach((colIndex) => {
      const value = parseNumericValue(row[colIndex]);
      if (value !== null && value !== 0) {
        const adjustedValue = value * contractMultiplier;
        const scaledValue = adjustedValue * contractSize;
        
        if (scaledValue < 0 && Math.abs(scaledValue) > Math.abs(worstLoss)) {
          worstLoss = scaledValue;
        } else if (scaledValue > 0 && scaledValue > highestProfit) {
          highestProfit = scaledValue;
        }
      }
    });
  });

  return { worstLoss, highestProfit };
}

const STATISTICS_OPTIONS = [
  { 
    value: 'pf', 
    label: 'Profit Factor', 
    shortLabel: 'PF', 
    format: 'number',
    description: 'Profit Factor measures the ratio of total profits to total losses. It indicates how much profit you make for every dollar lost.',
    explanation: 'Profit Factor is calculated by dividing total winning trades by total losing trades. A Profit Factor above 1.0 means you make more than you lose. For example, a PF of 2.0 means you make $2 for every $1 you lose. Higher is better - a PF of 3.0+ is considered excellent. If there were no losses in a period, cells show "No losses" because the ratio is undefined (you cannot divide by zero).',
    example: 'If you have $10,000 in total wins and $5,000 in total losses, your Profit Factor is 2.0 ($10,000 ÷ $5,000 = 2.0).'
  },
  { 
    value: 'winRate', 
    label: 'Win Rate (%)', 
    shortLabel: 'WR%', 
    format: 'percent',
    description: 'Win Rate shows the percentage of trading days that resulted in a profit.',
    explanation: 'Win Rate is calculated by dividing the number of winning days by the total number of trading days, then multiplying by 100. It shows how often your strategy is profitable. A higher win rate doesn\'t always mean better performance - a strategy with 40% win rate but high Profit Factor can be more profitable than one with 60% win rate.',
    example: 'If you trade 100 days and 55 of them are profitable, your Win Rate is 55% (55 ÷ 100 × 100 = 55%).'
  },
  { 
    value: 'avgWin', 
    label: 'Avg Win ($)', 
    shortLabel: 'AW$', 
    format: 'currency',
    description: 'Average Win shows the average profit per winning trading day.',
    explanation: 'Average Win is calculated by dividing total profits by the number of winning days. This metric helps you understand the typical size of your winning trades. Combined with Average Loss, it shows if your winners are larger than your losers, which is key to profitable trading.',
    example: 'If you have $10,000 in total wins across 20 winning days, your Average Win is $500 ($10,000 ÷ 20 = $500).'
  },
  { 
    value: 'avgLoss', 
    label: 'Avg Loss ($)', 
    shortLabel: 'AL$', 
    format: 'currency',
    description: 'Average Loss shows the average loss per losing trading day.',
    explanation: 'Average Loss is calculated by dividing total losses by the number of losing days. This metric helps you understand the typical size of your losing trades. Ideally, your Average Win should be larger than your Average Loss. Keeping losses small is crucial for long-term profitability.',
    example: 'If you have $5,000 in total losses across 25 losing days, your Average Loss is $200 ($5,000 ÷ 25 = $200).'
  },
  { 
    value: 'netPL', 
    label: 'Net P&L ($)', 
    shortLabel: 'P&L', 
    format: 'currency',
    description: 'Net P&L (Profit & Loss) shows the total profit or loss after subtracting losses from wins.',
    explanation: 'Net P&L is calculated by subtracting total losses from total wins. A positive value means overall profit, while a negative value means overall loss. This is the bottom-line metric that shows the actual dollar amount you\'ve made or lost.',
    example: 'If you have $10,000 in total wins and $5,000 in total losses, your Net P&L is $5,000 ($10,000 - $5,000 = $5,000).'
  },
  { 
    value: 'totalTrades', 
    label: 'Total Trades', 
    shortLabel: 'TT', 
    format: 'number',
    description: 'Total Trades shows the number of trading days with actual trading activity.',
    explanation: 'Total Trades counts all days where there was trading activity (both winning and losing days). Days with zero P&L are not counted. This metric helps you understand how active your strategy is and provides context for other statistics like Win Rate.',
    example: 'If you traded on 100 days out of 120 total days, your Total Trades is 100.'
  },
  { 
    value: 'blowoutRisk', 
    label: 'Blowout Risk', 
    shortLabel: 'BR%', 
    format: 'percent',
    description: 'Blowout Risk shows the probability that historical losses would exceed your maximum drawdown limit.',
    explanation: 'Blowout Risk calculates the percentage of historical trading days where losses (scaled by contract size) exceeded your maximum drawdown. This helps you understand the likelihood of account blowout based on historical strategy performance.',
    example: 'If 5 out of 100 historical trading days had losses exceeding your max drawdown, your Blowout Risk is 5%.'
  },
  { 
    value: 'thirtyPercentRule', 
    label: '% Rule', 
    shortLabel: '% Rule', 
    format: 'percent',
    description: '% Rule shows the probability that historical losses would exceed your configured consistency limit.',
    explanation: 'The % Rule calculates the percentage of historical trading days where losses exceeded the consistency limit based on your selected consistency percentage and risk inputs.',
    example: 'If 3 out of 100 historical trading days had losses exceeding the consistency limit, your % Rule risk is 3%.'
  }
];

/** When gross loss is $0, PF has no finite value; shown in table/cards instead of a symbol. */
const PROFIT_FACTOR_NO_LOSSES_LABEL = 'No losses';
const PROFIT_FACTOR_NO_LOSSES_TITLE =
  'No losing trades in this period. Profit factor is gross profit ÷ gross loss; when loss is $0 there is no finite ratio.';

/**
 * `personal`: the signed-in user's own accounts ("My portfolio") and own
 * strategies, already in the weekly sheet layout and in dollars:
 * { strategies: [{ strategy, success, group: 'portfolio' | 'mine' }], rawDataMap,
 *   loading, error, reload, note, headerAction, only }.
 * `only` (self users): there is no brand data, so nothing is fetched by name.
 * Otherwise they're listed beside the brand's strategies and can be mixed in.
 */
function PortfolioSimulator({ user, onUpgrade, personal = null }) {
  const personalOnly = Boolean(personal?.only);
  const [companyStrategies, setStrategies] = useState([]);
  /** Data names the server flagged Internal. Non-empty only for elevated viewers. */
  const [internalStrategyNames, setInternalStrategyNames] = useState([]);
  const internalStrategySet = useMemo(
    () => new Set((internalStrategyNames || []).map((n) => String(n).trim().toLowerCase())),
    [internalStrategyNames],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectUrl, setConnectUrl] = useState(null);
  const [selectedStatistic, setSelectedStatistic] = useState(() => {
    const stored = localStorage.getItem('portfolio.selectedStatistic');
    return stored || 'pf';
  });
  const [sortBy, setSortBy] = useState('overall'); // 'overall', 'monday', 'tuesday', etc.
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showFieldInfoModal, setShowFieldInfoModal] = useState(false);
  const [fieldInfoType, setFieldInfoType] = useState(null); // 'accountSize', 'contractSize', 'startOfDayProfit', 'safetyNet', 'maxDrawdown', 'currentBalance', 'profitSinceLastPayout', 'consistencyPercent'
  
  // Date range filter state
  const [dateRangeFilter, setDateRangeFilter] = useState(() => {
    const stored = localStorage.getItem('portfolio.dateRangeFilter');
    return stored || 'all-time';
  });
  const [customStartDate, setCustomStartDate] = useState(() => {
    return localStorage.getItem('portfolio.customStartDate') || '';
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    return localStorage.getItem('portfolio.customEndDate') || '';
  });
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  
  // Store raw strategy data for filtering
  const [companyRawData, setRawStrategyData] = useState({});

  // Brand strategies plus the user's own. A personal name that matches a
  // brand strategy gets " (mine)" so the two never share data.
  const { strategies, rawStrategyData, personalNameSet } = useMemo(() => {
    const company = companyStrategies.map((st) => ({ ...st, group: 'company' }));
    const taken = new Set(company.map((st) => String(st.strategy || '').toLowerCase()));
    const raw = { ...companyRawData };
    const mine = [];
    const names = new Set();
    (personal?.strategies || []).forEach((st) => {
      let name = st.strategy;
      if (taken.has(name.toLowerCase())) name = `${name} (mine)`;
      taken.add(name.toLowerCase());
      names.add(name);
      raw[name] = personal.rawDataMap?.[st.strategy];
      mine.push({ ...st, strategy: name });
    });
    return { strategies: [...company, ...mine], rawStrategyData: raw, personalNameSet: names };
  }, [companyStrategies, companyRawData, personal]);
  const isPersonalName = useCallback((name) => personalNameSet.has(name), [personalNameSet]);
  // Personal rows are already dollars (1×); brand sheets are NQ points, scaled
  // to MNQ by 0.1 ("BeefBowl … micro" is always MNQ).
  const multiplierFor = useCallback((name, type) => {
    if (name && personalNameSet.has(name)) return 1;
    const lower = String(name || '').toLowerCase();
    if (lower.includes('beefbowl') && lower.includes('micro')) return 0.1;
    return baseMultiplier(type);
  }, [personalNameSet]);
  
  // Simulation parameters
  const [contractSize, setContractSize] = useState(1);
  const [accountSize, setAccountSize] = useState(DEFAULT_ACCOUNT_SIZE);
  const [modalCurrentBalance, setModalCurrentBalance] = useState('');
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [modalStrategies, setModalStrategies] = useState(new Set()); // Strategies to show in modal
  const [showExplanationModal, setShowExplanationModal] = useState(false);
  const [explanationData, setExplanationData] = useState(null); // { type, title, explanation, values }
  const [hoveredCell, setHoveredCell] = useState(null); // { strategy, day, x, y }
  const [copyResultsFeedback, setCopyResultsFeedback] = useState(false);
  
  // Modal date range state (initialized from parent date range)
  const [modalDateRangeFilter, setModalDateRangeFilter] = useState(dateRangeFilter);
  const [modalCustomStartDate, setModalCustomStartDate] = useState(customStartDate);
  const [modalCustomEndDate, setModalCustomEndDate] = useState(customEndDate);
  const [showModalCustomDateModal, setShowModalCustomDateModal] = useState(false);
  const [portfolioDisplayFilter, setPortfolioDisplayFilter] = useState(() => ({
    ...DEFAULT_DISPLAY_FILTER,
    days: { ...DEFAULT_DISPLAY_FILTER.days },
  }));
  const [portfolioMergedHighImpactByDate, setPortfolioMergedHighImpactByDate] = useState(null);

  // Risk calculation inputs (persisted in localStorage)
  const [riskAccountSize, setRiskAccountSize] = useState(() => {
    const stored = localStorage.getItem('portfolio.riskAccountSize');
    return stored ? Number(stored) : DEFAULT_ACCOUNT_SIZE;
  });
  const [riskContractSize, setRiskContractSize] = useState(() => {
    const stored = localStorage.getItem('portfolio.riskContractSize');
    return stored ? Number(stored) : 1;
  });
  const [riskDrawdown, setRiskDrawdown] = useState(() => {
    const stored = localStorage.getItem('portfolio.riskDrawdown');
    return stored ? Number(stored) : 2500;
  });
  const [riskCurrentBalance, setRiskCurrentBalance] = useState(() => {
    return localStorage.getItem('portfolio.riskCurrentBalance') || '';
  });
  const [riskStartOfDayProfit, setRiskStartOfDayProfit] = useState(0);
  const [riskSafetyNet, setRiskSafetyNet] = useState(() => {
    const stored = localStorage.getItem('portfolio.riskSafetyNet');
    return stored ? Number(stored) : 0;
  });
  const [riskProfitSinceLastPayout, setRiskProfitSinceLastPayout] = useState(() => {
    const stored = localStorage.getItem('portfolio.riskProfitSinceLastPayout');
    return stored ? Number(stored) : 2600;
  });
  const [riskConsistencyPercent, setRiskConsistencyPercent] = useState(() => {
    const stored = Number(localStorage.getItem('portfolio.riskConsistencyPercent'));
    const userDefault = Number(user?.defaultConsistencyPercent);
    if ([20, 30, 40, 50, 55, 60].includes(stored)) return stored;
    if ([20, 30, 40, 50, 55, 60].includes(userDefault)) return userDefault;
    return 50;
  });
  const [riskCalculated, setRiskCalculated] = useState(false);
  const [analysisJustCompleted, setAnalysisJustCompleted] = useState(false);
  const [calculatingRisks, setCalculatingRisks] = useState(false);
  const [lastAnalysisRunAt, setLastAnalysisRunAt] = useState(null);
  const [showThirtyPercentModal, setShowThirtyPercentModal] = useState(false);
  const [showBlowoutRiskModal, setShowBlowoutRiskModal] = useState(false);
  const [showWindfallModal, setShowWindfallModal] = useState(false);
  
  // Input validation warnings
  const [inputWarnings, setInputWarnings] = useState({});
  
  // Per-strategy loading state for risk analysis
  const [strategyLoadingStates, setStrategyLoadingStates] = useState({}); // { strategyName: 'loading' | 'done' | 'error' }
  
  // Server-side analysis results (from /api/analyze)
  const [serverAnalysisResults, setServerAnalysisResults] = useState({}); // { strategyName: metrics }
  const [serverAnalysisLoading, setServerAnalysisLoading] = useState({}); // { strategyName: boolean }
  
  // Contract type selector (MNQ/NQ)
  // Personal data is already in dollars, so it uses the 1× (NQ) multiplier.
  const [contractType, setContractType] = useState(personalOnly ? 'NQ' : 'MNQ');
  // Modal-specific contract type (can differ from main view)
  const [modalContractType, setModalContractType] = useState(personalOnly ? 'NQ' : 'MNQ');
  // Contract size multiplier for table (avg win, avg loss, net P&L)
  const [simContractSize, setSimContractSize] = useState(() => {
    const stored = localStorage.getItem('portfolio.simContractSize');
    return stored ? parseInt(stored, 10) : 1;
  });

  const fetchAnalytics = useCallback(async () => {
    if (!user?.email) {
      setError('Please sign in to view portfolio analytics');
      return;
    }

    setLoading(true);
    setError(null);
    setConnectUrl(null);

    const fetchWithTimeout = async (url, options, timeoutMs = 120000) => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeoutMs);
      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } finally {
        window.clearTimeout(timer);
      }
    };

    try {
      const maxAttempts = 4;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        let response;
        try {
          response = await fetchWithTimeout(
            `${API_BASE_URL}/api/portfolio-analytics?email=${encodeURIComponent(user.email)}`,
            { headers: getBearerAuthHeaders() },
          );
        } catch (err) {
          if (err.name === 'AbortError') {
            if (attempt < maxAttempts) {
              await new Promise((resolve) => { setTimeout(resolve, 2000 * attempt); });
              continue;
            }
            throw new Error('Portfolio analytics timed out — the server may still be warming cache. Try again in a moment.');
          }
          throw err;
        }

        const contentType = response.headers.get('content-type') || '';
        const isJson = contentType.toLowerCase().includes('application/json');
        const data = isJson ? await response.json() : { success: false, error: await response.text() };

        if (response.status === 503 && attempt < maxAttempts) {
          const retryAfter = Number(response.headers.get('Retry-After') || 0);
          const waitMs = retryAfter > 0 ? retryAfter * 1000 : 2000 * attempt;
          await new Promise((resolve) => { setTimeout(resolve, waitMs); });
          continue;
        }

        if (response.status === 401 && data.requiresOAuth) {
          setConnectUrl(data.authUrl || null);
          setError(data.error || 'Connect Google to view portfolio analytics.');
          return;
        }

        if (!data.success) {
          if (data.requiresMembership) {
            throw new Error(data.error || 'Access denied. Active membership required.');
          }
          throw new Error(data.error || 'Failed to load portfolio analytics');
        }

        const rawDataMap = {};
        (data.strategies || []).forEach(strategy => {
          if (strategy.success && strategy.rawData) {
            rawDataMap[strategy.strategy] = strategy.rawData;
          }
        });
        setRawStrategyData(rawDataMap);
        setStrategies(data.strategies || []);
        setInternalStrategyNames(Array.isArray(data.internalStrategies) ? data.internalStrategies : []);
        return;
      }
      throw new Error('Failed to load portfolio analytics after several attempts.');
    } catch (err) {
      setError(err.message || 'Failed to load portfolio analytics');
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  // Fetch on mount
  useEffect(() => {
    if (personalOnly) return;
    if (user?.email) {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]); // fetchAnalytics is stable (memoized with useCallback)

  // Self users: loading and errors come from their own data, not a brand fetch.
  useEffect(() => {
    if (!personalOnly) return;
    setLoading(Boolean(personal.loading));
    setError(personal.error || null);
  }, [personalOnly, personal]);

  // Persist date range filter
  useEffect(() => {
    localStorage.setItem('portfolio.dateRangeFilter', dateRangeFilter);
  }, [dateRangeFilter]);

  useEffect(() => {
    localStorage.setItem('portfolio.customStartDate', customStartDate);
  }, [customStartDate]);

  useEffect(() => {
    localStorage.setItem('portfolio.customEndDate', customEndDate);
  }, [customEndDate]);

  useEffect(() => {
    if (simContractSize >= 1 && simContractSize <= 100) {
      localStorage.setItem('portfolio.simContractSize', simContractSize.toString());
    }
  }, [simContractSize]);

  // Get contract multiplier based on contract type and strategy name
  const getContractMultiplier = useCallback(
    (strategyName) => multiplierFor(strategyName, contractType),
    [multiplierFor, contractType],
  );

  // Filter strategies by date range and recalculate stats
  // IMPORTANT: Always recalculate with contract multiplier to match Vector Results view
  const filteredStrategies = useMemo(() => {
    const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
    
    return strategies.map(strategy => {
      if (!strategy.success || !rawStrategyData[strategy.strategy]) {
        return strategy; // Return as-is if no raw data
      }

      // Filter raw data by date range (or use all data if no filter)
      const filteredData = filterRowsByDateRange(rawStrategyData[strategy.strategy], startDate, endDate);
      
      // Get contract multiplier for this strategy
      const multiplier = getContractMultiplier(strategy.strategy);
      
      // Always recalculate stats from raw data with contract multiplier
      // This ensures consistency with Vector Results view which applies multiplier during display
      const statsByDay = calculateStrategyStatisticsByDay(filteredData, multiplier);
      
      return {
        ...strategy,
        statsByDay: statsByDay
      };
    });
  }, [strategies, dateRangeFilter, customStartDate, customEndDate, rawStrategyData, getContractMultiplier]);

  // Import calculateStrategyStatisticsByDay from utils or define it here
  // For now, I'll need to create a client-side version

  // Store risk calculations for each strategy
  const [riskCalculations, setRiskCalculations] = useState({}); // { strategyName: { blowoutRisk: {...}, thirtyPercentRule: {...} } }

  // Get statistic value for a strategy/day (applies contract size multiplier for avgWin, avgLoss, netPL)
  const getStatValue = useCallback((strategy, day) => {
    if (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') {
      const riskData = riskCalculations[strategy.strategy];
      if (!riskData) return null;
      const riskType = selectedStatistic === 'blowoutRisk' ? 'blowoutRisk' : 'thirtyPercentRule';
      const dayRisk = riskData[riskType]?.[day];
      return dayRisk ? dayRisk.probability : 0;
    }
    if (!strategy.statsByDay || !strategy.statsByDay[day]) return 0;
    let val = strategy.statsByDay[day][selectedStatistic] ?? 0;
    const mult = Math.max(1, parseInt(simContractSize, 10) || 1);
    if ((selectedStatistic === 'avgWin' || selectedStatistic === 'avgLoss' || selectedStatistic === 'netPL') && mult > 1) {
      val = val * mult;
    }
    return val;
  }, [selectedStatistic, riskCalculations, simContractSize]);

  // Sort and process strategies (filter out non-strategy sheets)
  const processedStrategies = useMemo(() => {
    let filtered = filteredStrategies.filter(s => {
      if (!s.success || !s.statsByDay) return false;
      // Filter out non-strategy sheets
      const nameLower = (s.strategy || '').toLowerCase().trim();
      if (NON_STRATEGY_SHEETS.has(nameLower)) return false;
      if (DATE_SHEET_PATTERN.test(nameLower)) return false;
      return true;
    });

    const strategyOrderMap =
      sortBy === 'strategy'
        ? new Map(
            sortStrategies(filtered.map((s) => s.strategy)).map((n, i) => [String(n).toLowerCase(), i]),
          )
        : null;

    // Sort strategies
    filtered.sort((a, b) => {
      if (sortBy === 'strategy' && strategyOrderMap) {
        const aIdx = strategyOrderMap.get(String(a.strategy || '').toLowerCase()) ?? 99999;
        const bIdx = strategyOrderMap.get(String(b.strategy || '').toLowerCase()) ?? 99999;
        const cmp = aIdx - bIdx;
        if (cmp !== 0) return sortOrder === 'desc' ? -cmp : cmp;
        const tie = String(a.strategy || '').localeCompare(String(b.strategy || ''));
        return sortOrder === 'desc' ? -tie : tie;
      }

      let aValue, bValue;
      
      if (sortBy === 'overall') {
        aValue = getStatValue(a, 'Overall');
        bValue = getStatValue(b, 'Overall');
      } else {
        const dayKey = sortBy.charAt(0).toUpperCase() + sortBy.slice(1);
        aValue = getStatValue(a, dayKey);
        bValue = getStatValue(b, dayKey);
      }

      // Profit factor: Infinity = no losses in period (shown as "No losses") = best PF, not "invalid"
      if (selectedStatistic === 'pf') {
        const aNoLosses = aValue === Infinity;
        const bNoLosses = bValue === Infinity;
        const aWorst =
          typeof aValue !== 'number' || (!Number.isFinite(aValue) && !aNoLosses) || aValue === 0;
        const bWorst =
          typeof bValue !== 'number' || (!Number.isFinite(bValue) && !bNoLosses) || bValue === 0;

        if (sortOrder === 'desc') {
          if (aNoLosses && bNoLosses) return 0;
          if (aNoLosses) return -1;
          if (bNoLosses) return 1;
          if (aWorst && bWorst) return 0;
          if (aWorst) return 1;
          if (bWorst) return -1;
          return bValue - aValue;
        }
        if (aNoLosses && bNoLosses) return 0;
        if (aNoLosses) return 1;
        if (bNoLosses) return -1;
        if (aWorst && bWorst) return 0;
        if (aWorst) return -1;
        if (bWorst) return 1;
        return aValue - bValue;
      } else {
        // For other statistics, handle Infinity normally
        if (aValue === Infinity && bValue === Infinity) return 0;
        if (aValue === Infinity) return -1;
        if (bValue === Infinity) return 1;

        if (sortOrder === 'desc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      }
    });

    // Add rank
    return filtered.map((strategy, index) => ({
      ...strategy,
      rank: index + 1
    }));
  }, [filteredStrategies, sortBy, sortOrder, selectedStatistic, getStatValue]);

  // Several picked: the simulation runs on their combined daily P&L. Rows are
  // summed in sheet units; personal rows (already dollars) are pre-scaled so
  // the contract-type multiplier applied later brings them back to dollars.
  const portfolioCombinedSheetRows = useMemo(() => {
    if (modalStrategies.size === 0) return [];
    const base = baseMultiplier(modalContractType);
    return combinePortfolioRawRows(Array.from(modalStrategies), rawStrategyData, (name) => (isPersonalName(name) ? 1 / base : 1));
  }, [modalStrategies, rawStrategyData, modalContractType, isPersonalName]);

  /** The rows and multiplier the modal's risk numbers use: one strategy's own, or the combined mix. */
  const modalRiskSource = useMemo(() => {
    const names = Array.from(modalStrategies);
    if (names.length > 1) return { rows: portfolioCombinedSheetRows, multiplier: baseMultiplier(modalContractType) };
    return { rows: names[0] ? rawStrategyData[names[0]] : null, multiplier: multiplierFor(names[0], modalContractType) };
  }, [modalStrategies, portfolioCombinedSheetRows, rawStrategyData, modalContractType, multiplierFor]);

  // Calculate combined performance for modal strategies (with modal date range)
  const combinedPerformance = useMemo(() => {
    if (modalStrategies.size === 0) return null;
    if (modalStrategies.size > 1) {
      // Net each day across the mix, then take the stats of that combined day.
      const range = getDateRange(modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate);
      return calculateStrategyStatisticsByDay(
        filterRowsByDateRange(modalRiskSource.rows || [], range.startDate, range.endDate),
        modalRiskSource.multiplier,
      );
    }

    // Filter strategies by modal date range
    const { startDate, endDate } = getDateRange(modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate);
    
    const selected = processedStrategies
      .filter(s => modalStrategies.has(s.strategy))
      .map(strategy => {
        // Always recalculate stats based on modal date range
        if (!rawStrategyData[strategy.strategy]) {
          return strategy; // Return as-is if no raw data
        }
        
        // Filter raw data by modal date range
        const filteredData = filterRowsByDateRange(rawStrategyData[strategy.strategy], startDate, endDate);
        
        // Get contract multiplier for this strategy (use modal contract type)
        const multiplier = multiplierFor(strategy.strategy, modalContractType);
        
        // Recalculate stats from filtered data with contract multiplier
        const statsByDay = calculateStrategyStatisticsByDay(filteredData, multiplier);
        
        return {
          ...strategy,
          statsByDay: statsByDay
        };
      });
    
    if (selected.length === 0) return null;

    const combined = {
      Monday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
      Tuesday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
      Wednesday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
      Thursday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
      Friday: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] },
      Overall: { wins: 0, losses: 0, winDays: 0, lossDays: 0, totalDays: 0, winValues: [], lossValues: [] }
    };

    // Aggregate data from all selected strategies
    selected.forEach(strategy => {
      ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Overall'].forEach(day => {
        const dayStats = strategy.statsByDay[day];
        if (dayStats) {
          // For combined, we need to aggregate wins/losses and counts
          // Since we have totals, we'll use the net values
          combined[day].wins += dayStats.wins || 0;
          combined[day].losses += dayStats.losses || 0;
          combined[day].totalDays += dayStats.totalTrades || 0;
          // Estimate win/loss days proportionally
          if (dayStats.totalTrades > 0) {
            const winRate = dayStats.winRate / 100;
            combined[day].winDays += Math.round(dayStats.totalTrades * winRate);
            combined[day].lossDays += Math.round(dayStats.totalTrades * (1 - winRate));
          }
        }
      });
    });

    // Calculate combined metrics
    const result = {};
    Object.keys(combined).forEach(dayName => {
      const stats = combined[dayName];
      const pf = stats.losses > 0 ? stats.wins / stats.losses : (stats.wins > 0 ? Infinity : 0);
      const winRate = stats.totalDays > 0 ? (stats.winDays / stats.totalDays) * 100 : 0;
      const avgWin = stats.winDays > 0 ? stats.wins / stats.winDays : 0;
      const avgLoss = stats.lossDays > 0 ? stats.losses / stats.lossDays : 0;
      const netPL = stats.wins - stats.losses;

      result[dayName] = {
        pf: isFinite(pf) ? Math.round(pf * 100) / 100 : Infinity,
        winRate: Math.round(winRate * 10) / 10,
        avgWin: Math.round(avgWin * 100) / 100,
        avgLoss: Math.round(avgLoss * 100) / 100,
        totalTrades: stats.totalDays,
        netPL: Math.round(netPL * 100) / 100
      };
    });

    return result;
  }, [modalStrategies, processedStrategies, modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate, rawStrategyData, modalContractType, modalRiskSource, multiplierFor]);

  // Calculate projected performance
  const projectedPerformance = useMemo(() => {
    if (!combinedPerformance || modalStrategies.size === 0) return null;

    const overall = combinedPerformance.Overall;
    if (!overall || overall.totalTrades === 0) return null;

    // Use contractSize and accountSize, defaulting to 1 and 50000 if empty
    const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
    const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;

    // Project based on average daily performance (already adjusted by contract multiplier in combinedPerformance)
    const avgDailyPL = overall.netPL / overall.totalTrades;
    const projectedDaily = avgDailyPL * effectiveContractSize;
    const projectedWeekly = projectedDaily * 5;
    const projectedMonthly = projectedWeekly * 4;
    const projectedYearly = projectedMonthly * 12;

    // Calculate as percentage of account size
    const dailyPercent = (projectedDaily / effectiveAccountSize) * 100;
    const weeklyPercent = (projectedWeekly / effectiveAccountSize) * 100;
    const monthlyPercent = (projectedMonthly / effectiveAccountSize) * 100;
    const yearlyPercent = (projectedYearly / effectiveAccountSize) * 100;

    return {
      daily: projectedDaily,
      weekly: projectedWeekly,
      monthly: projectedMonthly,
      yearly: projectedYearly,
      dailyPercent,
      weeklyPercent,
      monthlyPercent,
      yearlyPercent,
      winRate: overall.winRate,
      profitFactor: overall.pf
    };
  }, [combinedPerformance, contractSize, accountSize, modalStrategies.size]);

  // Calculate projected risks
  const projectedRisks = useMemo(() => {
    if (!combinedPerformance || modalStrategies.size === 0) return null;

    const overall = combinedPerformance.Overall;
    if (!overall || overall.totalTrades === 0) return null;

    const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
    const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
    const effectiveDrawdown = riskDrawdown === '' || riskDrawdown < 1 ? 2500 : riskDrawdown;
    const effectiveStartProfit = riskStartOfDayProfit === '' || riskStartOfDayProfit < 0 ? 0 : riskStartOfDayProfit;
    const effectiveSafetyNet = riskSafetyNet === '' || riskSafetyNet < 0 ? 0 : riskSafetyNet;
    const effectiveProfitSincePayout = riskProfitSinceLastPayout === '' || riskProfitSinceLastPayout < 0 ? 2600 : riskProfitSinceLastPayout;

    // Get date range for filtering
    const { startDate, endDate } = getDateRange(modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate);

    // Calculate risks for all selected strategies combined
    let blowoutRiskData = null;
    let thirtyPercentRuleData = null;
    let windfallData = null;

    const selected = processedStrategies.filter(s => modalStrategies.has(s.strategy));
    
    if (selected.length > 0 && modalRiskSource.rows) {
      // Use first strategy's data (or combine if needed)
      const strategyData = modalRiskSource.rows;
      const filteredData = filterRowsByDateRange(strategyData, startDate, endDate);
      
      // Get contract multiplier for this strategy (use modal contract type)
      const multiplier = modalRiskSource.multiplier;
      
      // Calculate blowout risk
      blowoutRiskData = calculateBlowoutRiskByDay(
        filteredData,
        effectiveAccountSize,
        effectiveContractSize,
        effectiveDrawdown,
        multiplier
      );

      // Calculate 30% rule consistency limit
      thirtyPercentRuleData = calculateThirtyPercentRuleByDay(
        filteredData,
        effectiveAccountSize,
        effectiveContractSize,
        effectiveStartProfit,
        effectiveSafetyNet,
        riskConsistencyPercent,
        multiplier
      );

      // Calculate windfall probability
      windfallData = calculateWindfallProbabilityByDay(
        filteredData,
        effectiveContractSize,
        effectiveProfitSincePayout,
        riskConsistencyPercent,
        multiplier
      );
    }

    return {
      blowoutRisk: blowoutRiskData?.Overall?.probability || 0,
      thirtyPercentRule: thirtyPercentRuleData?.Overall?.probability || 0,
      windfall: windfallData?.Overall?.probability || 0,
      thirtyPercentRuleData: thirtyPercentRuleData,
      blowoutRiskData: blowoutRiskData,
      windfallData: windfallData
    };
  }, [combinedPerformance, contractSize, accountSize, modalStrategies, modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate, processedStrategies, riskDrawdown, riskStartOfDayProfit, riskSafetyNet, riskProfitSinceLastPayout, riskConsistencyPercent, modalRiskSource]); // modalStrategies is a Set, included for .has() and .size checks

  // Calculate detailed 30% Rule metrics for modal
  const thirtyPercentRuleDetails = useMemo(() => {
    if (!projectedRisks?.thirtyPercentRuleData || modalStrategies.size === 0) return null;

    const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
    const effectiveStartProfit = riskStartOfDayProfit === '' || riskStartOfDayProfit < 0 ? 0 : riskStartOfDayProfit;
    const effectiveSafetyNet = riskSafetyNet === '' || riskSafetyNet < 0 ? 0 : riskSafetyNet;
    const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
    
    const overall = projectedRisks.thirtyPercentRuleData.Overall;
    if (!overall) return null;

    // Calculate consistency limit
    const profit = Number(effectiveStartProfit) || 0;
    const net = Number(effectiveSafetyNet) || 0;
    let baseAmount;
    if (profit <= net && net > 0) {
      baseAmount = net;
    } else {
      baseAmount = profit || net;
    }
    const limitPercent = normalizeConsistencyPercent(riskConsistencyPercent) / 100;
    const maeLimit = baseAmount * limitPercent;

    // Calculate worst loss, highest profit, and other metrics
    const selected = processedStrategies.filter(s => modalStrategies.has(s.strategy));
    let worstLoss = 0;
    let highestProfit = 0;
    let averageLoss = 0;
    let averageProfit = 0;
    let perContractLoss = 0;
    let totalDays = 0;
    let winningDays = 0;
    let losingDays = 0;
    
    if (selected.length > 0 && modalRiskSource.rows) {
      const strategyData = modalRiskSource.rows;
      const { startDate, endDate } = getDateRange(modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate);
      const filteredData = filterRowsByDateRange(strategyData, startDate, endDate);
      
      const multiplier = modalRiskSource.multiplier;
      
      const extremes = calculateHistoricalExtremes(filteredData, effectiveContractSize, multiplier);
      worstLoss = extremes.worstLoss;
      highestProfit = extremes.highestProfit;
      
      // Calculate additional metrics similar to blowout risk
      const rows = filteredData.slice(2);
      const dayColumns = [2, 3, 4, 5, 6];
      const allLosses = [];
      const allProfits = [];
      
      const parseNumericValue = (cellValue) => {
        if (cellValue === undefined || cellValue === null || cellValue === '') return null;
        const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
        const num = parseFloat(cleaned);
        return isNaN(num) ? null : num;
      };
      
      rows.forEach((row) => {
        if (!row || row.length < 7) return;
        dayColumns.forEach((colIndex) => {
          const value = parseNumericValue(row[colIndex]);
          if (value !== null && value !== 0) {
            totalDays++;
            const adjustedValue = value * multiplier;
            const scaledValue = adjustedValue * effectiveContractSize;
            
            if (value < 0) {
              allLosses.push(Math.abs(scaledValue));
              losingDays++;
            } else if (value > 0) {
              allProfits.push(scaledValue);
              winningDays++;
            }
          }
        });
      });
      
      if (allLosses.length > 0) {
        averageLoss = allLosses.reduce((a, b) => a + b, 0) / allLosses.length;
        perContractLoss = Math.max(...allLosses.map(l => l / effectiveContractSize));
      }
      if (allProfits.length > 0) {
        averageProfit = allProfits.reduce((a, b) => a + b, 0) / allProfits.length;
      }
    }

    const exceedsMae = worstLoss < 0 && Math.abs(worstLoss) > maeLimit;
    const maeBuffer = maeLimit - Math.abs(worstLoss);
    
    // Calculate largest safe strategy profit day for fastest payout (30% of $2,600 = $780)
    const APEX_MIN_PAYOUT_PROFIT = 2600;
    const largestSafeDayForFastestPayout = APEX_MIN_PAYOUT_PROFIT * (normalizeConsistencyPercent(riskConsistencyPercent) / 100);
    const isAligned = highestProfit <= largestSafeDayForFastestPayout;

    return {
      maeLimit,
      worstLoss: Math.abs(worstLoss),
      highestProfit,
      averageLoss,
      averageProfit,
      perContractLoss,
      breaches: overall.breaches,
      totalDays: overall.totalDays || totalDays,
      winningDays,
      losingDays,
      exceedsMae,
      maeBuffer,
      baseAmount,
      limitPercent: limitPercent * 100,
      largestSafeDayForFastestPayout,
      isAligned,
      accountSize: effectiveAccountSize
    };
  }, [projectedRisks, contractSize, accountSize, modalStrategies, processedStrategies, modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate, riskStartOfDayProfit, riskSafetyNet, riskConsistencyPercent, modalRiskSource]);

  // Calculate detailed Windfall metrics for modal
  const windfallDetails = useMemo(() => {
    if (!projectedRisks?.windfallData || modalStrategies.size === 0) return null;

    const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
    const effectiveProfitSincePayout = riskProfitSinceLastPayout === '' || riskProfitSinceLastPayout < 0 ? 2600 : riskProfitSinceLastPayout;
    const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
    
    const overall = projectedRisks.windfallData.Overall;
    if (!overall) return null;

    // Calculate largest safe day for fastest payout (30% of $2,600 = $780)
    const APEX_MIN_PAYOUT_PROFIT = 2600;
    const consistencyRatio = normalizeConsistencyPercent(riskConsistencyPercent) / 100;
    const largestSafeDayForFastestPayout = APEX_MIN_PAYOUT_PROFIT * consistencyRatio;
    const threshold = effectiveProfitSincePayout > 0 ? effectiveProfitSincePayout * consistencyRatio : largestSafeDayForFastestPayout;

    // Calculate highest profit and other metrics
    const selected = processedStrategies.filter(s => modalStrategies.has(s.strategy));
    let highestProfit = 0;
    let averageProfit = 0;
    let maxProfit = 0;
    let totalDays = 0;
    let winningDays = 0;
    let losingDays = 0;
    
    if (selected.length > 0 && modalRiskSource.rows) {
      const strategyData = modalRiskSource.rows;
      const { startDate, endDate } = getDateRange(modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate);
      const filteredData = filterRowsByDateRange(strategyData, startDate, endDate);
      
      const multiplier = modalRiskSource.multiplier;
      
      const extremes = calculateHistoricalExtremes(filteredData, effectiveContractSize, multiplier);
      highestProfit = extremes.highestProfit;
      
      // Calculate additional metrics
      const rows = filteredData.slice(2);
      const dayColumns = [2, 3, 4, 5, 6];
      const allProfits = [];
      
      const parseNumericValue = (cellValue) => {
        if (cellValue === undefined || cellValue === null || cellValue === '') return null;
        const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
        const num = parseFloat(cleaned);
        return isNaN(num) ? null : num;
      };
      
      rows.forEach((row) => {
        if (!row || row.length < 7) return;
        dayColumns.forEach((colIndex) => {
          const value = parseNumericValue(row[colIndex]);
          if (value !== null && value !== 0) {
            totalDays++;
            const adjustedValue = value * multiplier;
            const scaledValue = adjustedValue * effectiveContractSize;
            
            if (value < 0) {
              losingDays++;
            } else if (value > 0) {
              allProfits.push(scaledValue);
              winningDays++;
            }
          }
        });
      });
      
      if (allProfits.length > 0) {
        averageProfit = allProfits.reduce((a, b) => a + b, 0) / allProfits.length;
        maxProfit = Math.max(...allProfits);
      }
    }

    const isAligned = highestProfit <= largestSafeDayForFastestPayout;
    const exceedsThreshold = highestProfit > threshold;

    return {
      largestSafeDayForFastestPayout,
      threshold,
      highestProfit,
      averageProfit,
      maxProfit,
      breaches: overall.breaches,
      totalDays: overall.totalDays || totalDays,
      winningDays,
      losingDays,
      isAligned,
      exceedsThreshold,
      accountSize: effectiveAccountSize,
      profitSinceLastPayout: effectiveProfitSincePayout,
      probability: overall.probability || 0
    };
  }, [projectedRisks, contractSize, accountSize, modalStrategies, processedStrategies, modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate, riskProfitSinceLastPayout, riskConsistencyPercent, modalRiskSource]);

  /** Combined spreadsheet rows for all selected strategies (full history) — same shape as Vector Results */

  const portfolioFilteredSheetRows = useMemo(() => {
    if (portfolioCombinedSheetRows.length <= 2) return portfolioCombinedSheetRows;
    const { startDate, endDate } = getDateRange(modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate);
    return filterRowsByDateRange(portfolioCombinedSheetRows, startDate, endDate);
  }, [portfolioCombinedSheetRows, modalDateRangeFilter, modalCustomStartDate, modalCustomEndDate]);

  const portfolioSheetTitle = useMemo(
    () => (modalStrategies.size ? Array.from(modalStrategies).join(' + ') : 'Portfolio'),
    [modalStrategies],
  );

  const portfolioNeedsMergedImpact = useMemo(
    () =>
      isDisplayFilterActive(portfolioDisplayFilter) &&
      RED_FOLDER_FILTER_ENABLED && (portfolioDisplayFilter.redFolderOnly || portfolioDisplayFilter.nonRedFolderOnly),
    [portfolioDisplayFilter],
  );

  useEffect(() => {
    if (!portfolioNeedsMergedImpact) {
      setPortfolioMergedHighImpactByDate(null);
      return undefined;
    }
    setPortfolioMergedHighImpactByDate(null);
    const months = uniqueMonthKeysFromStrategyRows(portfolioFilteredSheetRows);
    if (months.length === 0) {
      setPortfolioMergedHighImpactByDate(new Map());
      return undefined;
    }
    let cancelled = false;
    Promise.all(months.map((mk) => loadUsHighImpactEvents(mk, ['ff-red']))).then((maps) => {
      if (cancelled) return;
      const merged = new Map();
      for (const m of maps) {
        m.forEach((arr, k) => merged.set(k, arr));
      }
      setPortfolioMergedHighImpactByDate(merged);
    });
    return () => {
      cancelled = true;
    };
  }, [portfolioNeedsMergedImpact, portfolioFilteredSheetRows]);

  const portfolioMetrics = useMemo(() => {
    if (!portfolioFilteredSheetRows || portfolioFilteredSheetRows.length === 0) return null;
    const base = computeStrategySheetMetrics(portfolioFilteredSheetRows);
    if (!isDisplayFilterActive(portfolioDisplayFilter)) return base;
    if (portfolioNeedsMergedImpact && portfolioMergedHighImpactByDate === null) return base;
    const impactMap = portfolioNeedsMergedImpact ? portfolioMergedHighImpactByDate || new Map() : new Map();
    const rollFilterEnabled = isNasdaqFuturesContractType(modalContractType);
    const includeDay = ({ dateKey, weekdayLabel }) => {
      const p = (dateKey || '').split('-');
      if (p.length !== 3) return false;
      const y = parseInt(p[0], 10);
      const mo = parseInt(p[1], 10) - 1;
      const d = parseInt(p[2], 10);
      if (isNaN(y) || isNaN(mo) || isNaN(d)) return false;
      const rollInfo = rollFilterEnabled ? getNasdaqRollWeekInfoForDay(y, mo, d) : { inRollWeek: false };
      return dayPassesDisplayFilter(dateKey, weekdayLabel, portfolioDisplayFilter, impactMap, {
        rollFilterEnabled,
        inRollWeek: !!rollInfo.inRollWeek,
      });
    };
    return computeStrategySheetMetrics(portfolioFilteredSheetRows, { includeDay });
  }, [
    portfolioFilteredSheetRows,
    portfolioDisplayFilter,
    portfolioNeedsMergedImpact,
    portfolioMergedHighImpactByDate,
    modalContractType,
  ]);

  const portfolioMetricsBaseline = useMemo(() => {
    if (!portfolioFilteredSheetRows || portfolioFilteredSheetRows.length === 0) return null;
    return computeStrategySheetMetrics(portfolioFilteredSheetRows);
  }, [portfolioFilteredSheetRows]);

  useEffect(() => {
    if (!showSimulationModal) return undefined;
    try {
      document.body.classList.add('portfolio-simulation-modal-open');
      return () => document.body.classList.remove('portfolio-simulation-modal-open');
    } catch (_) {
      return undefined;
    }
  }, [showSimulationModal]);

  // Calculate start-of-day profit when current balance or account size changes (30% Rule only)
  useEffect(() => {
    if (selectedStatistic === 'thirtyPercentRule' && riskCurrentBalance && riskAccountSize) {
      const currentBalance = Number(riskCurrentBalance);
      const accountSize = Number(riskAccountSize);
      const profit = Math.max(0, currentBalance - accountSize);
      setRiskStartOfDayProfit(profit);
    }
  }, [riskCurrentBalance, riskAccountSize, selectedStatistic]);

  // Auto-populate Safety Net based on account size (30% Rule only)
  useEffect(() => {
    if (selectedStatistic === 'thirtyPercentRule' && riskAccountSize) {
      const accountSizeNum = Number(riskAccountSize);
      const threshold = getThresholdForAccountSize(accountSizeNum);
      if (threshold !== null && threshold !== undefined) {
        setRiskSafetyNet(threshold);
      }
    }
  }, [riskAccountSize, selectedStatistic]);

  // Calculate start-of-day profit in modal when current balance or account size changes
  useEffect(() => {
    if (modalCurrentBalance && accountSize) {
      const currentBalance = Number(modalCurrentBalance);
      const accountSizeNum = Number(accountSize);
      const profit = Math.max(0, currentBalance - accountSizeNum);
      setRiskStartOfDayProfit(profit);
    }
  }, [modalCurrentBalance, accountSize]);

  // Auto-populate Safety Net in modal based on account size
  useEffect(() => {
    if (accountSize) {
      const accountSizeNum = Number(accountSize);
      const threshold = getThresholdForAccountSize(accountSizeNum);
      if (threshold !== null && threshold !== undefined) {
        setRiskSafetyNet(threshold);
      }
    }
  }, [accountSize]);

  // Show loading state when risk calculation inputs change
  useEffect(() => {
    if (showSimulationModal && modalStrategies.size > 0) {
      setCalculatingRisks(true);
      const timer = setTimeout(() => {
        setCalculatingRisks(false);
      }, 300); // Brief loading state for visual feedback
      return () => clearTimeout(timer);
    }
  }, [showSimulationModal, modalStrategies, contractSize, accountSize, riskDrawdown, riskStartOfDayProfit, riskSafetyNet, riskProfitSinceLastPayout, riskConsistencyPercent, modalContractType, modalDateRangeFilter]);

  // Persist risk settings in localStorage
  useEffect(() => {
    localStorage.setItem('portfolio.riskAccountSize', String(riskAccountSize));
  }, [riskAccountSize]);
  useEffect(() => {
    localStorage.setItem('portfolio.riskContractSize', String(riskContractSize));
  }, [riskContractSize]);
  useEffect(() => {
    localStorage.setItem('portfolio.riskDrawdown', String(riskDrawdown));
  }, [riskDrawdown]);
  useEffect(() => {
    localStorage.setItem('portfolio.riskCurrentBalance', String(riskCurrentBalance));
  }, [riskCurrentBalance]);
  useEffect(() => {
    localStorage.setItem('portfolio.riskSafetyNet', String(riskSafetyNet));
  }, [riskSafetyNet]);
  useEffect(() => {
    localStorage.setItem('portfolio.riskProfitSinceLastPayout', String(riskProfitSinceLastPayout));
  }, [riskProfitSinceLastPayout]);
  useEffect(() => {
    localStorage.setItem('portfolio.riskConsistencyPercent', String(riskConsistencyPercent));
  }, [riskConsistencyPercent]);
  useEffect(() => {
    const userDefault = Number(user?.defaultConsistencyPercent);
    if ([20, 30, 40, 50, 55, 60].includes(userDefault)) {
      setRiskConsistencyPercent(userDefault);
    }
  }, [user?.defaultConsistencyPercent]);

  // Auto-populate max drawdown when account size changes (Blowout Risk)
  useEffect(() => {
    if (selectedStatistic === 'blowoutRisk' && riskAccountSize) {
      const defaultDrawdown = DRAWDOWN_DEFAULTS[riskAccountSize];
      if (defaultDrawdown) {
        setRiskDrawdown(defaultDrawdown);
      }
    }
  }, [riskAccountSize, selectedStatistic]);

  // Input validation warnings
  useEffect(() => {
    const warnings = {};
    if (selectedStatistic === 'thirtyPercentRule') {
      if (riskCurrentBalance && riskAccountSize) {
        const balance = Number(riskCurrentBalance);
        const size = Number(riskAccountSize);
        if (balance < size) {
          warnings.currentBalance = `Warning: Current balance ($${balance.toLocaleString()}) is less than account size ($${size.toLocaleString()}). Start of day profit will be $0.`;
        }
      }
      if (!riskCurrentBalance || riskCurrentBalance === '' || Number(riskCurrentBalance) === 0) {
        warnings.currentBalance = 'Enter your current account balance to calculate risk accurately.';
      }
    }
    setInputWarnings(warnings);
  }, [riskCurrentBalance, riskAccountSize, selectedStatistic]);

  // ── Auto-run risk analysis on input change (debounced 800ms) ──
  const runRiskAnalysis = useCallback(() => {
    if (!processedStrategies || processedStrategies.length === 0) return;
    if (selectedStatistic !== 'blowoutRisk' && selectedStatistic !== 'thirtyPercentRule') return;
    
    const newCalculations = {};
    const loadingStates = {};
    const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
    
    // Mark all strategies as loading
    processedStrategies.forEach(strategy => {
      loadingStates[strategy.strategy] = 'loading';
    });
    setStrategyLoadingStates(loadingStates);

    processedStrategies.forEach(strategy => {
      if (!rawStrategyData[strategy.strategy]) {
        loadingStates[strategy.strategy] = 'error';
        return;
      }
      
      const filteredData = filterRowsByDateRange(rawStrategyData[strategy.strategy], startDate, endDate);
      const multiplier = getContractMultiplier(strategy.strategy);
      
      if (selectedStatistic === 'blowoutRisk') {
        const effectiveAccountSize = riskAccountSize === '' || riskAccountSize < 1000 ? 50000 : riskAccountSize;
        const effectiveContractSize = riskContractSize === '' || riskContractSize < 1 ? 1 : riskContractSize;
        const effectiveDrawdown = riskDrawdown === '' || riskDrawdown < 1 ? 2500 : riskDrawdown;
        
        const blowoutRisk = calculateBlowoutRiskByDay(
          filteredData,
          effectiveAccountSize,
          effectiveContractSize,
          effectiveDrawdown,
          multiplier
        );
        
        newCalculations[strategy.strategy] = {
          ...newCalculations[strategy.strategy],
          blowoutRisk
        };
      } else if (selectedStatistic === 'thirtyPercentRule') {
        const effectiveAccountSize = riskAccountSize === '' || riskAccountSize < 1000 ? 50000 : riskAccountSize;
        const effectiveContractSize = riskContractSize === '' || riskContractSize < 1 ? 1 : riskContractSize;
        const effectiveStartProfit = riskStartOfDayProfit === '' || riskStartOfDayProfit < 0 ? 0 : riskStartOfDayProfit;
        const effectiveSafetyNet = riskSafetyNet === '' || riskSafetyNet < 0 ? 0 : riskSafetyNet;
        
        const thirtyPercentRule = calculateThirtyPercentRuleByDay(
          filteredData,
          effectiveAccountSize,
          effectiveContractSize,
          effectiveStartProfit,
          effectiveSafetyNet,
          riskConsistencyPercent,
          multiplier
        );
        
        newCalculations[strategy.strategy] = {
          ...newCalculations[strategy.strategy],
          thirtyPercentRule
        };
      }
      loadingStates[strategy.strategy] = 'done';
    });
    
    setStrategyLoadingStates({ ...loadingStates });
    setRiskCalculations(newCalculations);
    setRiskCalculated(true);
    setLastAnalysisRunAt(new Date());
    
    // Trigger completion animation
    setAnalysisJustCompleted(true);
    setTimeout(() => {
      setAnalysisJustCompleted(false);
    }, 2000);
  }, [processedStrategies, rawStrategyData, selectedStatistic, riskAccountSize, riskContractSize, riskDrawdown, riskStartOfDayProfit, riskSafetyNet, riskConsistencyPercent, dateRangeFilter, customStartDate, customEndDate, getContractMultiplier]);

  // Also run server-side analysis for selected strategies (for enhanced metrics)
  const runServerAnalysis = useCallback(async (strategyName, overrides = {}) => {
    // The server analysis reads a brand sheet by name; personal data isn't one.
    if (isPersonalName(strategyName)) return null;
    setServerAnalysisLoading((prev) => ({ ...prev, [strategyName]: true }));
    try {
      const resolveNumber = (value, fallback) => {
        if (value === '' || value === null || value === undefined) return fallback;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      const effectiveAccountSize = resolveNumber(overrides.accountSize, riskAccountSize < 1000 ? 50000 : Number(riskAccountSize));
      const effectiveContractSize = resolveNumber(overrides.contracts, riskContractSize < 1 ? 1 : Number(riskContractSize));
      const effectiveDrawdown = resolveNumber(overrides.maxDrawdown, riskDrawdown < 1 ? 2500 : Number(riskDrawdown));
      const effectiveStartProfit = resolveNumber(overrides.startOfDayProfit, riskStartOfDayProfit < 0 ? 0 : Number(riskStartOfDayProfit));
      const effectiveSafetyNet = resolveNumber(overrides.safetyNet, riskSafetyNet < 0 ? 0 : Number(riskSafetyNet));
      const effectiveProfitSincePayout = resolveNumber(overrides.profitSinceLastPayout, riskProfitSinceLastPayout < 0 ? 2600 : Number(riskProfitSinceLastPayout));
      const effectiveConsistencyPercent = resolveNumber(overrides.consistencyPercent, normalizeConsistencyPercent(riskConsistencyPercent));
      const effectiveContractType = overrides.contractType || contractType;
      
      const response = await fetch(`${API_BASE_URL}/api/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getBearerAuthHeaders() },
        body: JSON.stringify({
          sheetName: strategyName,
          userEmail: user?.email || null,
          accountSize: effectiveAccountSize,
          contracts: effectiveContractSize,
          maxDrawdown: effectiveDrawdown,
          contractType: effectiveContractType,
          startOfDayProfit: effectiveStartProfit,
          safetyNet: effectiveSafetyNet,
          profitSinceLastPayout: effectiveProfitSincePayout,
          consistencyPercent: effectiveConsistencyPercent,
        }),
      });
      
      const data = await response.json();
      if (response.ok && data.success && data.metrics) {
        setServerAnalysisResults(prev => ({
          ...prev,
          [strategyName]: data.metrics,
        }));
        return data.metrics;
      }
      console.warn(`Server analysis unavailable for ${strategyName}:`, data?.error || response.statusText);
      return null;
    } catch (error) {
      console.error(`Error running server analysis for ${strategyName}:`, error);
      return null;
    } finally {
      setServerAnalysisLoading((prev) => ({ ...prev, [strategyName]: false }));
    }
  }, [riskAccountSize, riskContractSize, riskDrawdown, riskStartOfDayProfit, riskSafetyNet, riskProfitSinceLastPayout, riskConsistencyPercent, contractType, user?.email, isPersonalName]);

  // The server's extra metrics read one brand sheet by name, so they only apply
  // when a single brand strategy is open (not a mix, not the user's own data).
  const firstModalStrategy = useMemo(() => {
    const names = Array.from(modalStrategies);
    return names.length === 1 && !isPersonalName(names[0]) ? names[0] : null;
  }, [modalStrategies, isPersonalName]);
  const modalServerMetrics = firstModalStrategy ? serverAnalysisResults[firstModalStrategy] : null;

  useEffect(() => {
    if (!firstModalStrategy) return;
    if (!showBlowoutRiskModal && !showThirtyPercentModal) return;
    if (modalServerMetrics) return;

    runServerAnalysis(firstModalStrategy, {
      accountSize,
      contracts: contractSize,
      maxDrawdown: riskDrawdown,
      startOfDayProfit: riskStartOfDayProfit,
      safetyNet: riskSafetyNet,
      profitSinceLastPayout: riskProfitSinceLastPayout,
      consistencyPercent: riskConsistencyPercent,
      contractType: modalContractType,
    });
  }, [
    firstModalStrategy,
    showBlowoutRiskModal,
    showThirtyPercentModal,
    modalServerMetrics,
    runServerAnalysis,
    accountSize,
    contractSize,
    riskDrawdown,
    riskStartOfDayProfit,
    riskSafetyNet,
    riskProfitSinceLastPayout,
    riskConsistencyPercent,
    modalContractType,
  ]);

  const handleSort = (day) => {
    if (sortBy === day) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(day);
      setSortOrder('desc');
    }
  };

  // Source tabs: the brand's strategies ("Vector strategies"), the user's accounts, their own strategies.
  const groupCounts = useMemo(() => {
    const c = { company: 0, portfolio: 0, mine: 0 };
    processedStrategies.forEach((st) => { c[st.group || 'company'] = (c[st.group || 'company'] || 0) + 1; });
    return c;
  }, [processedStrategies]);
  const sourceTabs = useMemo(() => {
    const tabs = [];
    if (groupCounts.company) tabs.push({ id: 'company', label: `${getEntityDisplayName('Company')} strategies`, count: groupCounts.company });
    if (groupCounts.portfolio) tabs.push({ id: 'portfolio', label: 'My portfolio', count: groupCounts.portfolio });
    if (groupCounts.mine) tabs.push({ id: 'mine', label: 'My strategies', count: groupCounts.mine });
    if (tabs.length > 1) tabs.push({ id: 'all', label: 'All', count: processedStrategies.length });
    return tabs;
  }, [groupCounts, processedStrategies.length]);
  // Remembered across refresh; a tab that no longer has rows falls back below.
  const [sourceTab, setSourceTab] = usePersistentTab('portfolioSimulator.source', '', ['company', 'portfolio', 'mine', 'all']);
  const activeTab = sourceTabs.some((t) => t.id === sourceTab) ? sourceTab : (sourceTabs[0]?.id || 'all');
  const visibleStrategies = useMemo(() => {
    const list = activeTab === 'all' ? processedStrategies : processedStrategies.filter((st) => (st.group || 'company') === activeTab);
    return list.map((st, i) => ({ ...st, rank: i + 1 }));
  }, [processedStrategies, activeTab]);
  const nameColumnLabel = { company: 'Strategy', portfolio: 'Account', mine: 'Strategy' }[activeTab] || 'Name';

  // Rows ticked to simulate together; kept while switching tabs so a mix can
  // span brand strategies, accounts and the user's own strategies.
  const [mixSet, setMixSet] = useState(() => new Set());
  const toggleMix = (name) => setMixSet((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });
  useEffect(() => {
    // Drop ticks for rows that no longer exist (e.g. a deleted strategy).
    setMixSet((prev) => {
      const known = new Set(strategies.map((st) => st.strategy));
      const next = new Set([...prev].filter((n) => known.has(n)));
      return next.size === prev.size ? prev : next;
    });
  }, [strategies]);
  const groupOf = (name) => strategies.find((st) => st.strategy === name)?.group || 'company';

  const openSimulatorForStrategy = (strategyName) => {
    openSimulatorFor([strategyName]);
    trackEvent('strategy_opened', {
      strategy_name: strategyName,
      source: 'portfolio_simulator_table',
    });
  };

  const openSimulatorFor = (names) => {
    setModalStrategies(new Set(names));
    // Initialize modal date range and contract type from parent
    setModalDateRangeFilter(dateRangeFilter);
    setModalCustomStartDate(customStartDate);
    setModalCustomEndDate(customEndDate);
    setModalContractType(contractType); // Initialize with main view's contract type
    setShowSimulationModal(true);
  };

  // Helper function to determine if a value is "good" (green) or "bad" (red)
  const getValueColorClass = (value, statisticType) => {
    if (value === null || value === undefined || value === '-') return '';
    if (value === Infinity) {
      return statisticType === 'pf' ? 'positive' : '';
    }
    
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue)) return '';
    
    switch (statisticType) {
      case 'pf': // Profit Factor - >= 1.0 is good
        return numValue >= 1.0 ? 'positive' : 'negative';
      case 'winRate': // Win Rate - >= 50% is good
        return numValue >= 50 ? 'positive' : 'negative';
      case 'avgWin': // Avg Win - positive values are good
        return numValue > 0 ? 'positive' : '';
      case 'avgLoss': // Avg Loss - always red since losses are negative
        return numValue > 0 ? 'negative' : '';
      case 'netPL': // Net P&L - >= 0 is good
        return numValue >= 0 ? 'positive' : 'negative';
      case 'blowoutRisk': // Blowout Risk - lower is better
        return numValue < 5 ? 'positive' : numValue < 10 ? 'caution' : 'negative';
      case 'thirtyPercentRule': // 30% Rule - lower is better
        return numValue < 5 ? 'positive' : numValue < 10 ? 'caution' : 'negative';
      case 'totalTrades': // Total Trades - more is generally better
        return numValue > 0 ? 'positive' : '';
      default:
        return '';
    }
  };

  const formatValue = (value, format, statisticType = null) => {
    if (value === null || value === undefined) return '-';
    if (value === Infinity) {
      return statisticType === 'pf' ? PROFIT_FACTOR_NO_LOSSES_LABEL : '∞';
    }
    
    // Total Trades should always be whole numbers
    if (statisticType === 'totalTrades') {
      return Math.round(value).toString();
    }
    
    if (value === 0) return format === 'percent' ? '0.0%' : '0.00';
    
    switch (format) {
      case 'percent':
        return `${value.toFixed(1)}%`;
      case 'currency':
        return `$${value.toFixed(2)}`;
      case 'number':
      default:
        return value.toFixed(2);
    }
  };

  const getCurrentStatLabel = () => {
    return STATISTICS_OPTIONS.find(opt => opt.value === selectedStatistic)?.shortLabel || 'PF';
  };

  // Get all statistics for a specific day/strategy for tooltip
  const getDayStats = (strategy, day) => {
    if (!strategy.statsByDay || !strategy.statsByDay[day]) {
      return null;
    }
    return strategy.statsByDay[day];
  };

  // Handle cell hover
  const handleCellHover = (e, strategy, day) => {
    const rect = e.currentTarget.getBoundingClientRect();
    
    // Handle risk statistics (Blowout Risk, 30% Rule)
    if (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') {
      const riskData = riskCalculations[strategy.strategy];
      if (!riskData) return;
      
      const riskType = selectedStatistic === 'blowoutRisk' ? 'blowoutRisk' : 'thirtyPercentRule';
      const dayRisk = riskData[riskType]?.[day];
      if (!dayRisk) return;
      
      // Get overall metrics if available (for blowout risk, metrics are at the top level)
      const overallRisk = riskData[riskType]?.Overall;
      const metrics = riskData[riskType]?.metrics; // Blowout risk has metrics at top level
      
      // For 30% Rule, calculate additional metrics from raw data if available
      let thirtyPercentMetrics = null;
      if (riskType === 'thirtyPercentRule' && rawStrategyData[strategy.strategy]) {
        const strategyData = rawStrategyData[strategy.strategy];
        const { startDate, endDate } = getDateRange(dateRangeFilter, customStartDate, customEndDate);
        const filteredData = filterRowsByDateRange(strategyData, startDate, endDate);
        
        const effectiveContractSize = riskContractSize === '' || riskContractSize < 1 ? 1 : riskContractSize;
        const multiplier = multiplierFor(strategy.strategy, contractType);
        
        const extremes = calculateHistoricalExtremes(filteredData, effectiveContractSize, multiplier);
        
        // Calculate average loss and profit
        const rows = filteredData.slice(2);
        const dayColumns = [2, 3, 4, 5, 6];
        const allLosses = [];
        const allProfits = [];
        let totalDays = 0;
        let winningDays = 0;
        let losingDays = 0;
        
        const parseNumericValue = (cellValue) => {
          if (cellValue === undefined || cellValue === null || cellValue === '') return null;
          const cleaned = cellValue.toString().replace(/[$,]/g, '').trim();
          const num = parseFloat(cleaned);
          return isNaN(num) ? null : num;
        };
        
        rows.forEach((row) => {
          if (!row || row.length < 7) return;
          dayColumns.forEach((colIndex) => {
            const value = parseNumericValue(row[colIndex]);
            if (value !== null && value !== 0) {
              totalDays++;
              const adjustedValue = value * multiplier;
              const scaledValue = adjustedValue * effectiveContractSize;
              
              if (value < 0) {
                allLosses.push(Math.abs(scaledValue));
                losingDays++;
              } else if (value > 0) {
                allProfits.push(scaledValue);
                winningDays++;
              }
            }
          });
        });
        
        const averageLoss = allLosses.length > 0 ? allLosses.reduce((a, b) => a + b, 0) / allLosses.length : 0;
        const averageProfit = allProfits.length > 0 ? allProfits.reduce((a, b) => a + b, 0) / allProfits.length : 0;
        const perContractLoss = allLosses.length > 0 ? Math.max(...allLosses.map(l => l / effectiveContractSize)) : 0;
        const effectiveAccountSize = riskAccountSize === '' || riskAccountSize < 1000 ? 50000 : riskAccountSize;
        
        thirtyPercentMetrics = {
          worstLoss: Math.abs(extremes.worstLoss),
          highestProfit: extremes.highestProfit,
          averageLoss,
          averageProfit,
          perContractLoss,
          totalDays,
          winningDays,
          losingDays,
          accountSize: effectiveAccountSize
        };
      }
      
      setHoveredCell({
        strategy: strategy.strategy,
        day,
        x: rect.left + rect.width / 2,
        y: rect.top - 10,
        riskType,
        riskData: dayRisk,
        overallRisk,
        metrics,
        thirtyPercentMetrics,
        isRisk: true
      });
      return;
    }
    
    // Handle regular statistics
    const stats = getDayStats(strategy, day);
    if (!stats || stats.totalTrades === 0) return;
    
    setHoveredCell({
      strategy: strategy.strategy,
      day,
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      stats
    });
  };

  const handleCellLeave = () => {
    setHoveredCell(null);
  };

  if (connectUrl) {
    return (
      <div className="portfolio-simulator-container">
        <div className="portfolio-simulator-auth">
          <p>{error}</p>
          <a href={connectUrl} className="connect-button">
            Connect Google Sheets
          </a>
        </div>
      </div>
    );
  }

  if (error && !connectUrl) {
    return (
      <div className="portfolio-simulator-container">
        <div className="portfolio-simulator-error">
          <p>{error}</p>
          <button onClick={personalOnly ? personal.reload : fetchAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-simulator-container">
      <div className={`portfolio-simulator-header${personal?.headerAction ? ' has-action' : ''}`}>
        <p className="portfolio-simulator-subtitle">
          Analyze strategy performance by various statistics across different days of the week
        </p>
        {personal?.headerAction || null}
      </div>

      <div className="portfolio-simulator-data-notice" role="status">
        <span className="portfolio-simulator-data-notice-icon" aria-hidden="true">
          <IconInfo size={18} color="rgba(129, 140, 248, 0.95)" />
        </span>
        <span className="portfolio-simulator-data-notice-text">
          {personalOnly ? personal.note : getDataSourceNote()}
          {!personalOnly && personalNameSet.size > 0 ? ` ${personal.note}` : ''}
        </span>
      </div>

      <div className="portfolio-simulator-controls">
        <div className="control-group">
          <label htmlFor="statistic-select">Statistics:</label>
          <div className="statistic-select-wrapper">
            <select
              id="statistic-select"
              value={selectedStatistic}
              onChange={(e) => {
                const newStatistic = e.target.value;
                setSelectedStatistic(newStatistic);
                localStorage.setItem('portfolio.selectedStatistic', newStatistic);
                // Reset risk calculations when switching statistics
                if (newStatistic !== 'blowoutRisk' && newStatistic !== 'thirtyPercentRule') {
                  setRiskCalculated(false);
                  setRiskCalculations({});
                }
              }}
              className="control-select"
            >
              {STATISTICS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              className="info-icon-button"
              onClick={() => setShowInfoModal(true)}
              title="Learn about this statistic"
              aria-label="Show statistic information"
            >
              <IconInfo size={18} color="rgba(255, 255, 255, 0.7)" />
            </button>
          </div>
        </div>
        
        <div className="portfolio-sim-filter-row">
        {!personalOnly && (
        <div className="control-group">
          <label>Contract Type:</label>
          <div className="contract-type-chips">
            {['MNQ', 'NQ'].map((type) => (
              <button
                key={type}
                type="button"
                className={`contract-type-chip ${contractType === type ? 'active' : ''}`}
                onClick={() => {
                  setContractType(type);
                  if (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') {
                    setRiskCalculated(false);
                    setRiskCalculations({});
                  }
                }}
              >
                <span className="contract-type-chip-icon">{type}</span>
              </button>
            ))}
          </div>
        </div>
        )}

        {(selectedStatistic !== 'blowoutRisk' && selectedStatistic !== 'thirtyPercentRule') && (
          <div className="control-group">
            <div className="sim-contracts-label-row">
              <label htmlFor="sim-contract-size">{personalOnly ? 'Size Multiplier' : 'Default Contract Size Multiplier'}</label>
              <PnlContractsInfoButton
                className="sim-contract-label-info-btn"
                iconSize={14}
                iconColor="rgba(255, 255, 255, 0.65)"
              />
            </div>
            <input
              id="sim-contract-size"
              type="number"
              className="sim-contract-size-input"
              aria-label="Default Contract Size Multiplier (scales displayed values)"
              min="1"
              max="100"
              value={simContractSize}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  setSimContractSize('');
                  return;
                }
                const n = parseInt(v, 10);
                if (!isNaN(n) && n >= 1 && n <= 100) setSimContractSize(n);
              }}
              onBlur={(e) => {
                if (e.target.value === '' || parseInt(e.target.value, 10) < 1) {
                  setSimContractSize(1);
                }
              }}
              placeholder="1"
            />
          </div>
        )}

        <div className="control-group">
          <label htmlFor="date-range-filter">Date Range:</label>
          <select
            id="date-range-filter"
            className="control-select"
            aria-label="Select date range"
            value={dateRangeFilter}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'custom') {
                setShowCustomDateModal(true);
              } else {
                setDateRangeFilter(value);
                // Clear risk calculations when date range changes
                if (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') {
                  setRiskCalculated(false);
                  setRiskCalculations({});
                }
              }
            }}
          >
            <option value="all-time">All Time</option>
            <option value="last-30-days">Last 30 Days</option>
            <option value="last-3-months">Last 3 Months</option>
            <option value="last-6-months">Last 6 Months</option>
            <option value="last-12-months">Last 12 Months</option>
            <option value="2024">2024</option>
            <option value="2025">2025</option>
            <option value="custom">Custom Range...</option>
          </select>
        </div>
        </div>

      </div>

      {/* Risk Calculation Input Box */}
      {(selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') && (
        <div className="risk-calculation-box">
          <div className="risk-box-header">
            <h3 className="risk-box-title">
              {selectedStatistic === 'blowoutRisk' ? 'Blowout Risk Calculation' : `${riskConsistencyPercent}% Rule Calculation`}
            </h3>
            <div className="risk-analysis-button-container-inline">
              <button
                onClick={() => {
                  trackEvent('run_analysis_clicked', {
                    source: 'portfolio_simulator',
                    statistic: selectedStatistic,
                    strategy_count: processedStrategies.length,
                  });
                  runRiskAnalysis();
                }}
                className={`run-analysis-button ${analysisJustCompleted ? 'analysis-completed' : ''}`}
                disabled={loading}
              >
                {loading ? 'Loading...' : analysisJustCompleted ? 'Analysis Complete!' : 'Run Risk Analysis'}
              </button>
              {lastAnalysisRunAt && (
                <div className="risk-analysis-timestamp-inline">
                  Last updated: {lastAnalysisRunAt.toLocaleString()}
                </div>
              )}
            </div>
          </div>
          
          <div className={`risk-inputs-grid ${selectedStatistic === 'blowoutRisk' ? 'risk-inputs-grid-blowout' : 'risk-inputs-grid-thirty'}`}>
            <div className="risk-input-group">
              <label htmlFor="risk-account-size" className="risk-input-label-with-info">
                Account Size ($):
                <button
                  className="risk-info-icon-button"
                  onClick={() => {
                    setFieldInfoType('accountSize');
                    setShowFieldInfoModal(true);
                  }}
                  title="Learn about Account Size"
                  aria-label="Show Account Size information"
                >
                  <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                </button>
              </label>
              <div className="account-size-chips-stacked">
                <div className="account-size-chips">
                  {[25000, 50000, 100000].map((presetValue) => (
                    <button
                      key={presetValue}
                      type="button"
                      className={`account-size-chip ${riskAccountSize === presetValue ? 'active' : ''}`}
                      onClick={() => setRiskAccountSize(presetValue)}
                    >
                      {`${presetValue / 1000}K`}
                    </button>
                  ))}
                </div>
                <div className="account-size-chips">
                  {[150000, 250000, 300000].map((presetValue) => (
                    <button
                      key={presetValue}
                      type="button"
                      className={`account-size-chip ${riskAccountSize === presetValue ? 'active' : ''}`}
                      onClick={() => setRiskAccountSize(presetValue)}
                    >
                      {`${presetValue / 1000}K`}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="risk-input-group">
              <label htmlFor="risk-contract-size" className="risk-input-label-with-info">
                Default Contract Size Multiplier:
                <PnlContractsInfoButton
                  className="risk-info-icon-button"
                  iconSize={16}
                  iconColor="rgba(255, 255, 255, 0.7)"
                />
              </label>
              <input
                id="risk-contract-size"
                type="number"
                min="1"
                value={riskContractSize}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || val === null || val === undefined) {
                    setRiskContractSize('');
                  } else {
                    const num = parseInt(val);
                    if (!isNaN(num) && num >= 1) {
                      setRiskContractSize(num);
                    }
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value === '' || parseInt(e.target.value) < 1) {
                    setRiskContractSize(1);
                  }
                }}
                className="risk-input risk-input-contract-size"
              />
            </div>
            
            {selectedStatistic === 'blowoutRisk' ? (
              <div className="risk-input-group">
                <label htmlFor="risk-drawdown" className="risk-input-label-with-info">
                  Max Drawdown ($):
                  <button
                    className="risk-info-icon-button"
                    onClick={() => {
                      setFieldInfoType('maxDrawdown');
                      setShowFieldInfoModal(true);
                    }}
                    title="Learn about Max Drawdown"
                    aria-label="Show Max Drawdown information"
                  >
                    <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                  </button>
                </label>
                <input
                  id="risk-drawdown"
                  type="number"
                  min="1"
                  step="100"
                  value={riskDrawdown}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || val === null || val === undefined) {
                      setRiskDrawdown('');
                    } else {
                      const num = parseInt(val);
                      if (!isNaN(num) && num >= 1) {
                        setRiskDrawdown(num);
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '' || parseInt(e.target.value) < 1) {
                      setRiskDrawdown(2500);
                    }
                  }}
                  className="risk-input"
                />
              </div>
            ) : (
              <>
                <div className="risk-input-group">
                  <label className="risk-input-label-with-info">
                    % Rule:
                    <button
                      className="risk-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('consistencyPercent');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about % Rule"
                      aria-label="Show % Rule information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <select
                    className="risk-input"
                    value={riskConsistencyPercent}
                    onChange={(e) => setRiskConsistencyPercent(Number(e.target.value))}
                  >
                    {[20, 30, 40, 50, 55, 60].map((percent) => (
                      <option key={percent} value={percent}>
                        {percent}%
                      </option>
                    ))}
                  </select>
                </div>

                <div className="risk-input-group">
                  <label htmlFor="risk-current-balance" className="risk-input-label-with-info">
                    Current Account Balance ($):
                    <button
                      className="risk-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('accountSize');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Current Account Balance"
                      aria-label="Show Current Account Balance information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="risk-current-balance"
                    type="number"
                    min="0"
                    step="0.01"
                    value={riskCurrentBalance}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setRiskCurrentBalance('');
                      } else {
                        const num = parseFloat(val);
                        if (!isNaN(num) && num >= 0) {
                          setRiskCurrentBalance(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseFloat(e.target.value) < 0) {
                        setRiskCurrentBalance('');
                      }
                    }}
                    placeholder="52500"
                    className="risk-input"
                  />
                </div>
                
                <div className="risk-input-group">
                  <label htmlFor="risk-start-profit" className="risk-input-label-with-info">
                    Start of Day Profit ($):
                    <button
                      className="risk-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('startOfDayProfit');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Start of Day Profit"
                      aria-label="Show Start of Day Profit information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="risk-start-profit"
                    type="number"
                    min="0"
                    step="100"
                    value={riskStartOfDayProfit}
                    readOnly
                    className="risk-input"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                  <small className="risk-input-hint" style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                    Automatically calculated: Current Balance - Account Size = ${riskStartOfDayProfit || '0.00'}
                  </small>
                </div>
                
                <div className="risk-input-group">
                  <label htmlFor="risk-safety-net" className="risk-input-label-with-info">
                    Safety Net ($):
                    <button
                      className="risk-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('safetyNet');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Safety Net"
                      aria-label="Show Safety Net information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="risk-safety-net"
                    type="number"
                    min="0"
                    step="100"
                    value={riskSafetyNet}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setRiskSafetyNet('');
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 0) {
                          setRiskSafetyNet(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 0) {
                        setRiskSafetyNet(0);
                      }
                    }}
                    className="risk-input"
                  />
                </div>
              </>
            )}
          </div>
          
          {/* Input validation warnings */}
          {Object.keys(inputWarnings).length > 0 && (
            <div className="risk-input-warnings">
              {Object.entries(inputWarnings).map(([key, warning]) => (
                <div key={key} className="risk-input-warning">
                  <span className="warning-icon">⚠️</span> {warning}
                </div>
              ))}
            </div>
          )}
          
        </div>
      )}

      {/* Custom Date Range Modal */}
      {showCustomDateModal && ReactDOM.createPortal(
        <>
          <div className="video-modal-overlay" onClick={() => setShowCustomDateModal(false)} />
          <div className="custom-date-modal" onClick={(e) => e.stopPropagation()}>
            <div className="custom-date-header">
              <h2>Select Custom Date Range</h2>
              <button className="video-modal-close" onClick={() => setShowCustomDateModal(false)}>
                ×
              </button>
            </div>
            <div className="custom-date-body">
              <div className="custom-date-field">
                <label htmlFor="custom-start-date">Start Date</label>
                <input
                  id="custom-start-date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-field">
                <label htmlFor="custom-end-date">End Date</label>
                <input
                  id="custom-end-date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-actions">
                <button
                  className="custom-date-cancel"
                  onClick={() => {
                    setShowCustomDateModal(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="custom-date-apply"
                  onClick={() => {
                    if (customStartDate || customEndDate) {
                      setDateRangeFilter('custom');
                      setShowCustomDateModal(false);
                      // Clear risk calculations when date range changes
                      if (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') {
                        setRiskCalculated(false);
                        setRiskCalculations({});
                      }
                    }
                  }}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <div className="statistic-info-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="statistic-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="statistic-info-modal-header">
              <div className="statistic-info-modal-title">
                <IconInfo size={24} color={themeColor('#6366f1')} />
                <h3>
                  {STATISTICS_OPTIONS.find(opt => opt.value === selectedStatistic)?.label}
                </h3>
              </div>
              <button 
                className="statistic-info-modal-close" 
                onClick={() => setShowInfoModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="statistic-info-modal-content">
              <div className="statistic-info-description">
                <p>
                  {STATISTICS_OPTIONS.find(opt => opt.value === selectedStatistic)?.description}
                </p>
              </div>
              <div className="statistic-info-explanation">
                <h4>How it works:</h4>
                <p>
                  {STATISTICS_OPTIONS.find(opt => opt.value === selectedStatistic)?.explanation}
                </p>
              </div>
              <div className="statistic-info-example">
                <h4>Example:</h4>
                <p>
                  {STATISTICS_OPTIONS.find(opt => opt.value === selectedStatistic)?.example}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Field Info Modal */}
      {showFieldInfoModal && fieldInfoType && (
        <div className="statistic-info-modal-overlay" onClick={() => setShowFieldInfoModal(false)}>
          <div className="statistic-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="statistic-info-modal-header">
              <div className="statistic-info-modal-title">
                <IconInfo size={24} color={themeColor('#6366f1')} />
                <h3>
                  {fieldInfoType === 'accountSize' && 'Account Size'}
                  {fieldInfoType === 'contractSize' && 'Default Contract Size Multiplier'}
                  {fieldInfoType === 'startOfDayProfit' && 'Start Day Profit'}
                  {fieldInfoType === 'safetyNet' && 'Safety Net'}
                  {fieldInfoType === 'maxDrawdown' && 'Max Drawdown'}
                  {fieldInfoType === 'currentBalance' && 'Current Balance'}
                  {fieldInfoType === 'profitSinceLastPayout' && 'Since Last Payout'}
                </h3>
              </div>
              <button 
                className="statistic-info-modal-close" 
                onClick={() => setShowFieldInfoModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="statistic-info-modal-content">
              <div className="statistic-info-description">
                {fieldInfoType === 'contractSize' ? (
                  DEFAULT_PNL_SCALING_PARAGRAPHS.map((text, i) => (
                    <p key={i} className={i > 0 ? 'statistic-info-description-paragraph-follow' : undefined}>
                      {text}
                    </p>
                  ))
                ) : (
                  <p>
                    {fieldInfoType === 'accountSize' && 'Enter your total account balance or equity. This is used to calculate risk percentages and determine if losses exceed your account size. For Apex Trader Funding accounts, use your account size (e.g., $50,000 for a 50K account).'}
                    {fieldInfoType === 'startOfDayProfit' && 'This field is automatically calculated from your Current Balance minus Account Size. It represents your profit above the original account size at the start of the trading day. This value is used to calculate the consistency limit for your selected % Rule.'}
                    {fieldInfoType === 'safetyNet' && 'Enter your trailing threshold or safety net amount. This is the minimum profit level you want to maintain. The consistency limit is calculated as your selected % of the higher value between Start of Day Profit and Safety Net.'}
                    {fieldInfoType === 'maxDrawdown' && 'Enter your maximum acceptable drawdown in dollars. This is the maximum loss you\'re willing to accept before stopping trading. The Blowout Risk calculation shows the percentage of historical trading days where losses (scaled by contract size) exceeded this drawdown limit. For example, if you set $2,500 as your max drawdown, the calculation will show how often historical losses exceeded this amount.'}
                    {fieldInfoType === 'currentBalance' && 'Enter your current account balance or equity. This is your total account value right now, including any profits or losses. When combined with your Account Size, this automatically calculates your Start Day Profit. For example, if you started with $50,000 and now have $52,500, enter 52500.'}
                    {fieldInfoType === 'profitSinceLastPayout' && 'Enter the total profit you\'ve accumulated since your last payout. This is used for Windfall Risk calculations so no single trading day accounts for more than your selected % of total profit.'}
                    {fieldInfoType === 'consistencyPercent' && 'Choose the consistency percentage used across % Rule calculations. Supported options are 20%, 30%, 40%, 50%, and 60%.'}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Simulation Modal */}
      {showSimulationModal && modalStrategies.size > 0 && ReactDOM.createPortal(
        <div className="simulation-modal-overlay" onClick={() => setShowSimulationModal(false)}>
          <div className="simulation-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="simulation-modal-header">
              <h3>Portfolio Simulation</h3>
              <button 
                className="simulation-modal-close"
                onClick={() => setShowSimulationModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="simulation-modal-date-range">
              <label htmlFor="modal-date-range-filter">Date Range:</label>
              <select
                id="modal-date-range-filter"
                className="simulation-modal-select"
                value={modalDateRangeFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'custom') {
                    setShowModalCustomDateModal(true);
                  } else {
                    setModalDateRangeFilter(value);
                  }
                }}
              >
                <option value="all-time">All Time</option>
                <option value="last-30-days">Last 30 Days</option>
                <option value="last-3-months">Last 3 Months</option>
                <option value="last-6-months">Last 6 Months</option>
                <option value="last-12-months">Last 12 Months</option>
                <option value="2024">2024</option>
                <option value="2025">2025</option>
                <option value="custom">Custom Range...</option>
              </select>
            </div>
            
            <div className="simulation-controls">
              {/* Strategy & Contract Configuration Group */}
              <div className="simulation-param-group account-contract-group">
                <div className="simulation-param-group-title">Account & Contract</div>
                <div className="simulation-param">
                  <label>Selected Strategies ({modalStrategies.size}):</label>
                  <div className="selected-strategies-list">
                    {Array.from(modalStrategies).map(name => (
                      <span key={name} className="strategy-tag">
                        {name}
                      </span>
                    ))}
                  </div>
                </div>
                
                {!personalOnly && (
                <div className="simulation-param">
                  <label>Contract Type:</label>
                  <div className="contract-type-chips">
                    {['MNQ', 'NQ'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        className={`contract-type-chip ${modalContractType === type ? 'active' : ''}`}
                        onClick={() => setModalContractType(type)}
                      >
                        <span className="contract-type-chip-icon">{type}</span>
                      </button>
                    ))}
                  </div>
                </div>
                )}
                
                <div className="simulation-param">
                  <label htmlFor="modal-contract-size" className="simulation-label-with-info">
                    Default Contract Size Multiplier:
                    <button
                      type="button"
                      className="simulation-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('contractSize');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Default Contract Size Multiplier"
                      aria-label="Show Default Contract Size Multiplier information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="modal-contract-size"
                    type="number"
                    min="1"
                    value={contractSize}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setContractSize('');
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 1) {
                          setContractSize(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 1) {
                        setContractSize(1);
                      }
                    }}
                    className="simulation-input"
                  />
                </div>
                
                <div className="simulation-param">
                  <label htmlFor="modal-account-size">Account Size ($):</label>
                  <div className="account-size-chips-stacked">
                    <div className="account-size-chips">
                      {[25000, 50000, 100000].map((presetValue) => (
                        <button
                          key={presetValue}
                          type="button"
                          className={`account-size-chip ${accountSize === presetValue ? 'active' : ''}`}
                          onClick={() => setAccountSize(presetValue)}
                        >
                          {`${presetValue / 1000}K`}
                        </button>
                      ))}
                    </div>
                    <div className="account-size-chips">
                      {[150000, 250000, 300000].map((presetValue) => (
                        <button
                          key={presetValue}
                          type="button"
                          className={`account-size-chip ${accountSize === presetValue ? 'active' : ''}`}
                          onClick={() => setAccountSize(presetValue)}
                        >
                          {`${presetValue / 1000}K`}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Risk Parameters Group */}
              <div className="simulation-param-group risk-params-group">
                <div className="simulation-param-group-title">Risk Parameters</div>
                <div className="simulation-param">
                  <label htmlFor="modal-current-balance" className="simulation-label-with-info">
                    Current Balance ($):
                    <button
                      className="simulation-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('currentBalance');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Current Balance"
                      aria-label="Show Current Balance information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="modal-current-balance"
                    type="number"
                    min="0"
                    step="0.01"
                    value={modalCurrentBalance}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setModalCurrentBalance('');
                      } else {
                        const num = parseFloat(val);
                        if (!isNaN(num) && num >= 0) {
                          setModalCurrentBalance(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseFloat(e.target.value) < 0) {
                        setModalCurrentBalance('');
                      }
                    }}
                    placeholder="52500"
                    className="simulation-input"
                  />
                </div>
                
                <div className="simulation-param">
                  <label htmlFor="modal-safety-net" className="simulation-label-with-info">
                    Safety Net ($):
                    <button
                      className="simulation-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('safetyNet');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Safety Net"
                      aria-label="Show Safety Net information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="modal-safety-net"
                    type="number"
                    min="0"
                    step="100"
                    value={riskSafetyNet}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setRiskSafetyNet('');
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 0) {
                          setRiskSafetyNet(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 0) {
                        setRiskSafetyNet(0);
                      }
                    }}
                    className="simulation-input"
                  />
                </div>
                
                <div className="simulation-param">
                  <label htmlFor="modal-profit-payout" className="simulation-label-with-info">
                    Since Last Payout ($):
                    <button
                      className="simulation-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('profitSinceLastPayout');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Profit Since Last Payout"
                      aria-label="Show Profit Since Last Payout information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="modal-profit-payout"
                    type="number"
                    min="0"
                    step="100"
                    value={riskProfitSinceLastPayout}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setRiskProfitSinceLastPayout('');
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 0) {
                          setRiskProfitSinceLastPayout(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 0) {
                        setRiskProfitSinceLastPayout(2600);
                      }
                    }}
                    className="simulation-input"
                  />
                </div>
                
                <div className="simulation-param">
                  <label htmlFor="modal-drawdown" className="simulation-label-with-info">
                    Max Drawdown ($):
                    <button
                      className="simulation-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('maxDrawdown');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Max Drawdown"
                      aria-label="Show Max Drawdown information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="modal-drawdown"
                    type="number"
                    min="1"
                    step="100"
                    value={riskDrawdown}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || val === null || val === undefined) {
                        setRiskDrawdown('');
                      } else {
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 1) {
                          setRiskDrawdown(num);
                        }
                      }
                    }}
                    onBlur={(e) => {
                      if (e.target.value === '' || parseInt(e.target.value) < 1) {
                        setRiskDrawdown(2500);
                      }
                    }}
                    className="simulation-input"
                  />
                </div>
                
                <div className="simulation-param">
                  <label htmlFor="modal-start-profit" className="simulation-label-with-info">
                    Start Day Profit ($):
                    <button
                      className="simulation-info-icon-button"
                      onClick={() => {
                        setFieldInfoType('startOfDayProfit');
                        setShowFieldInfoModal(true);
                      }}
                      title="Learn about Start Day Profit"
                      aria-label="Show Start Day Profit information"
                    >
                      <IconInfo size={16} color="rgba(255, 255, 255, 0.7)" />
                    </button>
                  </label>
                  <input
                    id="modal-start-profit"
                    type="number"
                    min="0"
                    step="100"
                    value={riskStartOfDayProfit}
                    readOnly
                    className="simulation-input"
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', cursor: 'not-allowed' }}
                  />
                  <small className="simulation-field-hint">
                    Automatically calculated: Current Balance - Account Size = ${riskStartOfDayProfit || '0.00'}
                  </small>
                </div>
              </div>
            </div>

            {calculatingRisks && (
              <div className="simulation-loading-overlay">
                <div className="simulation-loading-spinner"></div>
                <p>Calculating performance metrics...</p>
              </div>
            )}
            
            {combinedPerformance && projectedPerformance && (
              <div className="simulation-results">
                <h4>Combined Performance</h4>
                <div className="combined-stats-grid">
                  <div 
                    className={`combined-stat-card clickable-stat-card ${projectedPerformance.winRate >= 50 ? 'positive' : 'negative'}`}
                    onClick={() => {
                      const overall = combinedPerformance.Overall;
                      setExplanationData({
                        type: 'winRate',
                        title: 'Overall Win Rate',
                        value: `${projectedPerformance.winRate.toFixed(1)}%`,
                        explanation: `Win Rate = (Winning Days / Total Trading Days) × 100`,
                        calculation: `(${overall.winDays || 0} winning days / ${overall.totalTrades || 0} total trading days) × 100 = ${projectedPerformance.winRate.toFixed(1)}%`,
                        details: `Out of ${overall.totalTrades || 0} total trading days across all selected strategies, ${overall.winDays || 0} were profitable.`
                      });
                      setShowExplanationModal(true);
                    }}
                  >
                    <div className="combined-stat-label">Overall Win Rate</div>
                    <div className={`combined-stat-value ${projectedPerformance.winRate >= 50 ? 'positive' : 'negative'}`}>
                      {projectedPerformance.winRate.toFixed(1)}%
                    </div>
                  </div>
                  <div 
                    className={`combined-stat-card clickable-stat-card ${projectedPerformance.profitFactor >= 1 ? 'positive' : 'negative'}`}
                    onClick={() => {
                      const overall = combinedPerformance.Overall;
                      const isInfinity = projectedPerformance.profitFactor === Infinity || !isFinite(projectedPerformance.profitFactor);
                      setExplanationData({
                        type: 'profitFactor',
                        title: 'Overall Profit Factor',
                        value: formatValue(projectedPerformance.profitFactor, 'number', 'pf'),
                        explanation: `Profit Factor = Total Wins / Total Losses`,
                        calculation: isInfinity 
                          ? `$${overall.wins?.toFixed(2) || 0} wins ÷ $${overall.losses?.toFixed(2) || 0} losses — with $0 in losses there is no finite ratio, so we show "${PROFIT_FACTOR_NO_LOSSES_LABEL}".`
                          : `$${overall.wins?.toFixed(2) || 0} total wins / $${overall.losses?.toFixed(2) || 0} total losses = ${formatValue(projectedPerformance.profitFactor, 'number', 'pf')}`,
                        details: isInfinity 
                          ? `There were no losing trades in this data, so gross loss is $0. That is strong performance; the label "${PROFIT_FACTOR_NO_LOSSES_LABEL}" replaces a number you cannot compute (division by zero).`
                          : `A Profit Factor above 1.0 means you make more than you lose. ${projectedPerformance.profitFactor >= 1 ? 'This is profitable!' : 'This indicates losses exceed wins.'}`
                      });
                      setShowExplanationModal(true);
                    }}
                  >
                    <div className="combined-stat-label">Overall Profit Factor</div>
                    <div
                      className={`combined-stat-value ${projectedPerformance.profitFactor >= 1 ? 'positive' : 'negative'}`}
                      title={projectedPerformance.profitFactor === Infinity ? PROFIT_FACTOR_NO_LOSSES_TITLE : undefined}
                    >
                      {formatValue(projectedPerformance.profitFactor, 'number', 'pf')}
                    </div>
                  </div>
                </div>

                <div className="projected-performance-section">
                  <h4>Projected Performance</h4>
                  <div className="projected-stats-grid">
                  <div 
                    className={`projected-stat-card clickable-stat-card ${projectedPerformance.daily >= 0 ? 'positive' : 'negative'}`}
                    onClick={() => {
                      const overall = combinedPerformance.Overall;
                      const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
                      const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
                      const avgDailyPL = overall.netPL / overall.totalTrades;
                      const projectedDaily = avgDailyPL * effectiveContractSize;
                      const dailyPercent = (projectedDaily / effectiveAccountSize) * 100;
                      setExplanationData({
                        type: 'daily',
                        title: 'Daily Projected Performance',
                        value: `$${projectedPerformance.daily.toFixed(2)} (${projectedPerformance.dailyPercent.toFixed(2)}%)`,
                        explanation: `Daily Projection = (Average Daily P&L) × Contract Size`,
                        calculation: `(${overall.netPL?.toFixed(2) || 0} total net P&L / ${overall.totalTrades || 0} trading days) × ${effectiveContractSize} contracts = $${projectedDaily.toFixed(2)}`,
                        percentageExplanation: `Percentage = (Daily Projection / Account Size) × 100`,
                        percentageCalculation: `($${projectedDaily.toFixed(2)} / $${effectiveAccountSize.toLocaleString()}) × 100 = ${dailyPercent.toFixed(2)}%`,
                        details: `Based on historical performance, this strategy is projected to make ${projectedDaily >= 0 ? 'a profit' : 'a loss'} of $${Math.abs(projectedDaily).toFixed(2)} per day on average.`
                      });
                      setShowExplanationModal(true);
                    }}
                  >
                    <div className="projected-stat-label">Daily</div>
                    <div className={`projected-stat-value ${projectedPerformance.daily >= 0 ? 'positive' : 'negative'}`}>
                      ${projectedPerformance.daily.toFixed(2)}
                    </div>
                    <div className={`projected-stat-percent ${projectedPerformance.dailyPercent >= 0 ? 'positive' : 'negative'}`}>
                      ({projectedPerformance.dailyPercent.toFixed(2)}%)
                    </div>
                  </div>
                  <div 
                    className={`projected-stat-card clickable-stat-card ${projectedPerformance.weekly >= 0 ? 'positive' : 'negative'}`}
                    onClick={() => {
                      const overall = combinedPerformance.Overall;
                      const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
                      const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
                      const avgDailyPL = overall.netPL / overall.totalTrades;
                      const projectedDaily = avgDailyPL * effectiveContractSize;
                      const projectedWeekly = projectedDaily * 5;
                      const weeklyPercent = (projectedWeekly / effectiveAccountSize) * 100;
                      setExplanationData({
                        type: 'weekly',
                        title: 'Weekly Projected Performance',
                        value: `$${projectedPerformance.weekly.toFixed(2)} (${projectedPerformance.weeklyPercent.toFixed(2)}%)`,
                        explanation: `Weekly Projection = Daily Projection × 5 trading days`,
                        calculation: `$${projectedDaily.toFixed(2)} daily × 5 days = $${projectedWeekly.toFixed(2)}`,
                        percentageExplanation: `Percentage = (Weekly Projection / Account Size) × 100`,
                        percentageCalculation: `($${projectedWeekly.toFixed(2)} / $${effectiveAccountSize.toLocaleString()}) × 100 = ${weeklyPercent.toFixed(2)}%`,
                        details: `Based on 5 trading days per week, this strategy is projected to make ${projectedWeekly >= 0 ? 'a profit' : 'a loss'} of $${Math.abs(projectedWeekly).toFixed(2)} per week on average.`
                      });
                      setShowExplanationModal(true);
                    }}
                  >
                    <div className="projected-stat-label">Weekly</div>
                    <div className={`projected-stat-value ${projectedPerformance.weekly >= 0 ? 'positive' : 'negative'}`}>
                      ${projectedPerformance.weekly.toFixed(2)}
                    </div>
                    <div className={`projected-stat-percent ${projectedPerformance.weeklyPercent >= 0 ? 'positive' : 'negative'}`}>
                      ({projectedPerformance.weeklyPercent.toFixed(2)}%)
                    </div>
                  </div>
                  <div 
                    className={`projected-stat-card clickable-stat-card ${projectedPerformance.monthly >= 0 ? 'positive' : 'negative'}`}
                    onClick={() => {
                      const overall = combinedPerformance.Overall;
                      const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
                      const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
                      const avgDailyPL = overall.netPL / overall.totalTrades;
                      const projectedDaily = avgDailyPL * effectiveContractSize;
                      const projectedWeekly = projectedDaily * 5;
                      const projectedMonthly = projectedWeekly * 4;
                      const monthlyPercent = (projectedMonthly / effectiveAccountSize) * 100;
                      setExplanationData({
                        type: 'monthly',
                        title: 'Monthly Projected Performance',
                        value: `$${projectedPerformance.monthly.toFixed(2)} (${projectedPerformance.monthlyPercent.toFixed(2)}%)`,
                        explanation: `Monthly Projection = Weekly Projection × 4 weeks`,
                        calculation: `$${projectedWeekly.toFixed(2)} weekly × 4 weeks = $${projectedMonthly.toFixed(2)}`,
                        percentageExplanation: `Percentage = (Monthly Projection / Account Size) × 100`,
                        percentageCalculation: `($${projectedMonthly.toFixed(2)} / $${effectiveAccountSize.toLocaleString()}) × 100 = ${monthlyPercent.toFixed(2)}%`,
                        details: `Based on 4 weeks per month, this strategy is projected to make ${projectedMonthly >= 0 ? 'a profit' : 'a loss'} of $${Math.abs(projectedMonthly).toFixed(2)} per month on average.`
                      });
                      setShowExplanationModal(true);
                    }}
                  >
                    <div className="projected-stat-label">Monthly</div>
                    <div className={`projected-stat-value ${projectedPerformance.monthly >= 0 ? 'positive' : 'negative'}`}>
                      ${projectedPerformance.monthly.toFixed(2)}
                    </div>
                    <div className={`projected-stat-percent ${projectedPerformance.monthlyPercent >= 0 ? 'positive' : 'negative'}`}>
                      ({projectedPerformance.monthlyPercent.toFixed(2)}%)
                    </div>
                  </div>
                  <div 
                    className={`projected-stat-card clickable-stat-card ${projectedPerformance.yearly >= 0 ? 'positive' : 'negative'}`}
                    onClick={() => {
                      const overall = combinedPerformance.Overall;
                      const effectiveContractSize = contractSize === '' || contractSize < 1 ? 1 : contractSize;
                      const effectiveAccountSize = accountSize === '' || accountSize < 1000 ? 50000 : accountSize;
                      const avgDailyPL = overall.netPL / overall.totalTrades;
                      const projectedDaily = avgDailyPL * effectiveContractSize;
                      const projectedWeekly = projectedDaily * 5;
                      const projectedMonthly = projectedWeekly * 4;
                      const projectedYearly = projectedMonthly * 12;
                      const yearlyPercent = (projectedYearly / effectiveAccountSize) * 100;
                      setExplanationData({
                        type: 'yearly',
                        title: 'Yearly Projected Performance',
                        value: `$${projectedPerformance.yearly.toFixed(2)} (${projectedPerformance.yearlyPercent.toFixed(2)}%)`,
                        explanation: `Yearly Projection = Monthly Projection × 12 months`,
                        calculation: `$${projectedMonthly.toFixed(2)} monthly × 12 months = $${projectedYearly.toFixed(2)}`,
                        percentageExplanation: `Percentage = (Yearly Projection / Account Size) × 100`,
                        percentageCalculation: `($${projectedYearly.toFixed(2)} / $${effectiveAccountSize.toLocaleString()}) × 100 = ${yearlyPercent.toFixed(2)}%`,
                        details: `Based on 12 months per year, this strategy is projected to make ${projectedYearly >= 0 ? 'a profit' : 'a loss'} of $${Math.abs(projectedYearly).toFixed(2)} per year on average.`
                      });
                      setShowExplanationModal(true);
                    }}
                  >
                    <div className="projected-stat-label">Yearly</div>
                    <div className={`projected-stat-value ${projectedPerformance.yearly >= 0 ? 'positive' : 'negative'}`}>
                      ${projectedPerformance.yearly.toFixed(2)}
                    </div>
                    <div className={`projected-stat-percent ${projectedPerformance.yearlyPercent >= 0 ? 'positive' : 'negative'}`}>
                      ({projectedPerformance.yearlyPercent.toFixed(2)}%)
                    </div>
                  </div>
                </div>
                </div>
              </div>
            )}

            {/* Projected Risk Section */}
            {projectedRisks && (
              <div className="simulation-results projected-risk-section">
                <h4>Projected Risk</h4>
                <div className="projected-risk-grid">
                  <div 
                    className={`projected-risk-card clickable-stat-card ${projectedRisks.blowoutRisk <= 5 ? 'positive' : projectedRisks.blowoutRisk <= 10 ? 'caution' : 'negative'}`}
                    onClick={() => {
                      if (projectedRisks?.blowoutRiskData?.metrics) {
                        setShowBlowoutRiskModal(true);
                      }
                    }}
                  >
                    <div className="projected-risk-label">Blowout Risk</div>
                    <div className={`projected-risk-value-wrapper ${projectedRisks.blowoutRisk <= 5 ? 'positive' : projectedRisks.blowoutRisk <= 10 ? 'caution' : 'negative'}`}>
                      <div className={`projected-risk-value ${projectedRisks.blowoutRisk <= 5 ? 'positive' : projectedRisks.blowoutRisk <= 10 ? 'caution' : 'negative'}`}>
                        {projectedRisks.blowoutRisk.toFixed(1)}%
                      </div>
                      {projectedRisks.blowoutRisk === 0 && (
                        <IconCheck size={16} color="#10b981" className="zero-risk-indicator" />
                      )}
                    </div>
                  </div>
                  
                  <div 
                    className={`projected-risk-card clickable-stat-card ${projectedRisks.thirtyPercentRule <= 5 ? 'positive' : projectedRisks.thirtyPercentRule <= 10 ? 'caution' : 'negative'}`}
                    onClick={() => {
                      if (thirtyPercentRuleDetails) {
                        setShowThirtyPercentModal(true);
                      }
                    }}
                  >
                    <div className="projected-risk-label">% Rule</div>
                    <div className={`projected-risk-value-wrapper ${projectedRisks.thirtyPercentRule <= 5 ? 'positive' : projectedRisks.thirtyPercentRule <= 10 ? 'caution' : 'negative'}`}>
                      <div className={`projected-risk-value ${projectedRisks.thirtyPercentRule <= 5 ? 'positive' : projectedRisks.thirtyPercentRule <= 10 ? 'caution' : 'negative'}`}>
                        {projectedRisks.thirtyPercentRule.toFixed(1)}%
                      </div>
                      {projectedRisks.thirtyPercentRule === 0 && (
                        <IconCheck size={16} color="#10b981" className="zero-risk-indicator" />
                      )}
                    </div>
                  </div>
                  
                  <div 
                    className={`projected-risk-card clickable-stat-card ${projectedRisks.windfall <= 5 ? 'positive' : projectedRisks.windfall <= 10 ? 'caution' : 'negative'}`}
                    onClick={() => {
                      if (windfallDetails) {
                        setShowWindfallModal(true);
                      }
                    }}
                  >
                    <div className="projected-risk-label">Windfall</div>
                    <div className={`projected-risk-value-wrapper ${projectedRisks.windfall <= 5 ? 'positive' : projectedRisks.windfall <= 10 ? 'caution' : 'negative'}`}>
                      <div className={`projected-risk-value ${projectedRisks.windfall <= 5 ? 'positive' : projectedRisks.windfall <= 10 ? 'caution' : 'negative'}`}>
                        {projectedRisks.windfall.toFixed(1)}%
                      </div>
                      {projectedRisks.windfall === 0 && (
                        <IconCheck size={16} color="#10b981" className="zero-risk-indicator" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Strategy Performance — same StrategyDetailsView as Vector Results (calendar / chart / weekly, filters, date range) */}
            {portfolioFilteredSheetRows.length > 2 && (
              <div className="simulation-results simulation-strategy-performance simulation-strategy-performance--details">
                <StrategyDetailsView
                  sheetName={portfolioSheetTitle}
                  metrics={portfolioMetrics}
                  metricsBaseline={portfolioMetricsBaseline}
                  rawRows={portfolioFilteredSheetRows}
                  rawRowsForCalendar={portfolioCombinedSheetRows}
                  contractType={modalContractType}
                  contractSize={contractSize === '' || contractSize < 1 ? 1 : Number(contractSize)}
                  displayFilter={portfolioDisplayFilter}
                  onDisplayFilterChange={setPortfolioDisplayFilter}
                  vectorDateRangeFilter={modalDateRangeFilter}
                  onVectorDateRangeChange={(value) => {
                    if (value === 'custom') setShowModalCustomDateModal(true);
                    else setModalDateRangeFilter(value);
                  }}
                />
              </div>
            )}

          </div>
        </div>,
        document.body
      )}

      {/* Modal Custom Date Range Modal */}
      {showModalCustomDateModal && ReactDOM.createPortal(
        <>
          <div className="video-modal-overlay" onClick={() => setShowModalCustomDateModal(false)} />
          <div className="custom-date-modal" onClick={(e) => e.stopPropagation()}>
            <div className="custom-date-header">
              <h2>Select Custom Date Range</h2>
              <button className="video-modal-close" onClick={() => setShowModalCustomDateModal(false)}>
                ×
              </button>
            </div>
            <div className="custom-date-body">
              <div className="custom-date-field">
                <label htmlFor="modal-custom-start-date">Start Date</label>
                <input
                  id="modal-custom-start-date"
                  type="date"
                  value={modalCustomStartDate}
                  onChange={(e) => setModalCustomStartDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-field">
                <label htmlFor="modal-custom-end-date">End Date</label>
                <input
                  id="modal-custom-end-date"
                  type="date"
                  value={modalCustomEndDate}
                  onChange={(e) => setModalCustomEndDate(e.target.value)}
                  className="custom-date-input"
                />
              </div>
              <div className="custom-date-actions">
                <button
                  className="custom-date-cancel"
                  onClick={() => {
                    setShowModalCustomDateModal(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="custom-date-apply"
                  onClick={() => {
                    if (modalCustomStartDate && modalCustomEndDate) {
                      setModalDateRangeFilter('custom');
                    }
                    setShowModalCustomDateModal(false);
                  }}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* % Rule Details Modal */}
      {showThirtyPercentModal && thirtyPercentRuleDetails && ReactDOM.createPortal(
        <div className="performance-explanation-modal-overlay" onClick={() => setShowThirtyPercentModal(false)}>
          <div className="performance-explanation-modal-content thirty-percent-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="performance-explanation-modal-header">
              <h3>{riskConsistencyPercent}% Rule Analysis</h3>
              <button 
                className="performance-explanation-modal-close"
                onClick={() => setShowThirtyPercentModal(false)}
              >
                ×
              </button>
            </div>
            <div className="performance-explanation-modal-body">
              {/* Status Header */}
              {thirtyPercentRuleDetails.exceedsMae && (
                <div className="thirty-percent-status-banner warning">
                  <span className="status-banner-icon">▲</span>
                  <span className="status-banner-text">REQUIRES ATTENTION</span>
                </div>
              )}
              {modalServerMetrics?.riskCalculationNote && (
                <div className="thirty-percent-safe-box" style={{ marginBottom: '1rem' }}>
                  <div className="safe-content">
                    <div className="safe-title">How RiskLo Chooses Risk Inputs</div>
                    <div className="safe-message">{modalServerMetrics.riskCalculationNote}</div>
                    {modalServerMetrics.riskCalculationMethod === 'max_historical_vs_sl' && (
                      <div className="safe-message" style={{ marginTop: '0.5rem' }}>
                        Historical loss: ${Number(modalServerMetrics.historicalHighestLoss || 0).toFixed(2)} | SL-based estimate: ${Number(modalServerMetrics.theoreticalStopLoss || 0).toFixed(2)} | Risk basis used: ${Number(modalServerMetrics.riskBasisLoss || modalServerMetrics.highestLoss || 0).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {firstModalStrategy && serverAnalysisLoading[firstModalStrategy] && (
                <div className="thirty-percent-safe-box" style={{ marginBottom: '1rem' }}>
                  <div className="safe-content">
                    <div className="safe-title">Loading Enhanced SL Metrics...</div>
                  </div>
                </div>
              )}
              {modalServerMetrics?.hasMultipleTpTargets && modalServerMetrics?.multiTpModelingNote && (
                <div className="thirty-percent-warning-box" style={{ marginBottom: '1rem' }}>
                  <div className="warning-content">
                    <div className="warning-title">Multi-TP Modeling Note</div>
                    <div className="warning-message">{modalServerMetrics.multiTpModelingNote}</div>
                  </div>
                </div>
              )}
              
              {/* Main Metrics Grid */}
              <div className="thirty-percent-details-grid">
                <div className={`thirty-percent-detail-card ${thirtyPercentRuleDetails.exceedsMae ? 'warning' : 'safe'}`}>
                  <div className="thirty-percent-detail-label">
                    Maximum Loss Allowed (Consistency Limit)
                    {thirtyPercentRuleDetails.exceedsMae && <span className="detail-badge warning-badge">REQUIRES ATTENTION</span>}
                  </div>
                  <div className={`thirty-percent-detail-value ${thirtyPercentRuleDetails.exceedsMae ? 'negative' : 'positive'}`}>
                    ${thirtyPercentRuleDetails.maeLimit.toFixed(2)}
                  </div>
                  <div className="thirty-percent-detail-subtext">
                    {thirtyPercentRuleDetails.limitPercent}% of ${thirtyPercentRuleDetails.baseAmount.toFixed(2)}
                  </div>
                </div>
                
                <div className={`thirty-percent-detail-card ${thirtyPercentRuleDetails.isAligned ? 'safe' : ''}`}>
                  <div className="thirty-percent-detail-label">
                    Largest Safe Strategy Profit Day for Fastest Payout
                    {thirtyPercentRuleDetails.isAligned && <span className="detail-badge safe-badge">ALIGNED</span>}
                  </div>
                  <div className="thirty-percent-detail-value positive">
                    ${thirtyPercentRuleDetails.largestSafeDayForFastestPayout.toFixed(2)}
                  </div>
                  <div className="thirty-percent-detail-subtext">
                    If the strategy's largest profit day stays under this level, a payout at $2,600 is achievable.
                  </div>
                </div>
                
                <div className="thirty-percent-detail-card">
                  <div className="thirty-percent-detail-label">Strategy's Worst Historical Loss Day</div>
                  <div className={`thirty-percent-detail-value ${thirtyPercentRuleDetails.exceedsMae ? 'negative' : ''}`}>
                    ${thirtyPercentRuleDetails.worstLoss.toFixed(2)}
                  </div>
                  <div className="thirty-percent-detail-subtext">
                    {((thirtyPercentRuleDetails.worstLoss / thirtyPercentRuleDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                    {modalServerMetrics?.highestLossDate && (
                      <span style={{ display: 'block', marginTop: '0.25rem' }}>Worst: {modalServerMetrics.highestLossDate}</span>
                    )}
                  </div>
                </div>
                
                <div className="thirty-percent-detail-card">
                  <div className="thirty-percent-detail-label">Strategy's Highest Historical Profit Day</div>
                  <div className="thirty-percent-detail-value positive">
                    ${thirtyPercentRuleDetails.highestProfit.toFixed(2)}
                  </div>
                  <div className="thirty-percent-detail-subtext">
                    {((thirtyPercentRuleDetails.highestProfit / thirtyPercentRuleDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                    {modalServerMetrics?.maxProfitDate && (
                      <span style={{ display: 'block', marginTop: '0.25rem' }}>Best: {modalServerMetrics.maxProfitDate}</span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Additional Metrics Grid */}
              <div className="thirty-percent-metrics-grid">
                <div className="thirty-percent-metric-card">
                  <div className="thirty-percent-metric-icon">📊</div>
                  <div className="thirty-percent-metric-label">AVERAGE LOSS</div>
                  <div className="thirty-percent-metric-value negative">
                    ${thirtyPercentRuleDetails.averageLoss.toFixed(2)}
                  </div>
                  <div className="thirty-percent-metric-detail">
                    {((thirtyPercentRuleDetails.averageLoss / thirtyPercentRuleDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>
                {modalServerMetrics?.theoreticalStopLoss !== null && modalServerMetrics?.theoreticalStopLoss !== undefined && (
                  <div className="thirty-percent-metric-card">
                    <div className="thirty-percent-metric-icon">🛡️</div>
                    <div className="thirty-percent-metric-label">SL-BASED THEORETICAL LOSS</div>
                    <div className="thirty-percent-metric-value negative">
                      ${Number(modalServerMetrics.theoreticalStopLoss).toFixed(2)}
                    </div>
                    <div className="thirty-percent-metric-detail">
                      {modalServerMetrics.theoreticalStopLossPercent || '0.00'}% of account ({modalServerMetrics.strategySlLimitPoints || 0} pts SL)
                    </div>
                  </div>
                )}
                
                <div className="thirty-percent-metric-card">
                  <div className="thirty-percent-metric-icon">📈</div>
                  <div className="thirty-percent-metric-label">AVERAGE PROFIT</div>
                  <div className="thirty-percent-metric-value positive">
                    ${thirtyPercentRuleDetails.averageProfit.toFixed(2)}
                  </div>
                  <div className="thirty-percent-metric-detail">
                    {((thirtyPercentRuleDetails.averageProfit / thirtyPercentRuleDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>
                
                <div className="thirty-percent-metric-card">
                  <div className="thirty-percent-metric-icon">👥</div>
                  <div className="thirty-percent-metric-label">PER-CONTRACT LOSS</div>
                  <div className="thirty-percent-metric-value negative">
                    ${thirtyPercentRuleDetails.perContractLoss.toFixed(2)}
                  </div>
                  <div className="thirty-percent-metric-detail">Worst loss per contract</div>
                </div>
                
                <div className="thirty-percent-metric-card">
                  <div className="thirty-percent-metric-icon">📅</div>
                  <div className="thirty-percent-metric-label">TRADING DAYS ANALYZED</div>
                  <div className="thirty-percent-metric-value">
                    {thirtyPercentRuleDetails.totalDays}
                  </div>
                  <div className="thirty-percent-metric-detail">
                    {thirtyPercentRuleDetails.winningDays} winning, {thirtyPercentRuleDetails.losingDays} losing days
                  </div>
                </div>
              </div>
              
              {/* Warning/Safe Box */}
              {thirtyPercentRuleDetails.exceedsMae && (
                <div className="thirty-percent-warning-box">
                  <div className="warning-icon">▲</div>
                  <div className="warning-content">
                    <div className="warning-title">WARNING (Consistency):</div>
                    <div className="warning-message">
                      The strategy's worst historical loss day exceeds the consistency limit by ${Math.abs(thirtyPercentRuleDetails.maeBuffer).toFixed(2)}.
                      This strategy has historically violated the {riskConsistencyPercent}% rule.
                    </div>
                    <div className="warning-probability">
                      Probability of exceeding consistency limit: <strong style={{ color: '#ef4444' }}>{projectedRisks.thirtyPercentRule.toFixed(2)}%</strong>
                      <span className="warning-breaches">
                        ({thirtyPercentRuleDetails.breaches} of {thirtyPercentRuleDetails.totalDays} trading days exceeded the limit)
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              {!thirtyPercentRuleDetails.exceedsMae && thirtyPercentRuleDetails.maeLimit > 0 && (
                <div className="thirty-percent-safe-box">
                  <div className="safe-icon">✓</div>
                  <div className="safe-content">
                    <div className="safe-title">SAFE:</div>
                    <div className="safe-message">
                      Historical worst loss (${thirtyPercentRuleDetails.worstLoss.toFixed(2)}) is within consistency limit (${thirtyPercentRuleDetails.maeLimit.toFixed(2)}).
                      Buffer: ${thirtyPercentRuleDetails.maeBuffer.toFixed(2)}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Server-side enhanced metrics for % Rule */}
              {(() => {
                const serverMetrics = modalServerMetrics;
                
                if (serverMetrics && serverMetrics.apexMaeComparison) {
                  return (
                    <div className="server-metrics-section">
                      <h4 className="server-metrics-title">Server Analysis (Enhanced)</h4>
                      <div className="thirty-percent-metrics-grid">
                        <div className="thirty-percent-metric-card">
                          <div className="thirty-percent-metric-icon">🎯</div>
                          <div className="thirty-percent-metric-label">CONSISTENCY STATUS</div>
                          <div className={`thirty-percent-metric-value ${serverMetrics.apexMaeComparison.maeStatus === 'SAFE' ? 'positive' : 'negative'}`}>
                            {serverMetrics.apexMaeComparison.maeStatus}
                          </div>
                          <div className="thirty-percent-metric-detail">
                            {serverMetrics.apexMaeComparison.maeMessage}
                          </div>
                        </div>
                        <div className="thirty-percent-metric-card">
                          <div className="thirty-percent-metric-icon">📊</div>
                          <div className="thirty-percent-metric-label">CONSISTENCY BREACH PROB.</div>
                          <div className={`thirty-percent-metric-value ${serverMetrics.apexMaeComparison.maeBreachProbability === 0 ? 'positive' : 'negative'}`}>
                            {serverMetrics.apexMaeComparison.maeBreachProbability.toFixed(1)}%
                          </div>
                          <div className="thirty-percent-metric-detail">
                            {serverMetrics.apexMaeComparison.maeBreaches} of {serverMetrics.apexMaeComparison.totalTradingDays} days
                          </div>
                        </div>
                        <div className="thirty-percent-metric-card">
                          <div className="thirty-percent-metric-icon">🛡️</div>
                          <div className="thirty-percent-metric-label">CONSISTENCY BUFFER</div>
                          <div className={`thirty-percent-metric-value ${Number(serverMetrics.apexMaeComparison.maeBuffer) >= 0 ? 'positive' : 'negative'}`}>
                            ${serverMetrics.apexMaeComparison.maeBuffer}
                          </div>
                          <div className="thirty-percent-metric-detail">
                            Max consistency limit: ${serverMetrics.apexMaeComparison.maxMaePerTrade.toFixed(2)}
                          </div>
                        </div>
                        <div className="thirty-percent-metric-card">
                          <div className="thirty-percent-metric-icon">⚡</div>
                          <div className="thirty-percent-metric-label">RISK LEVEL</div>
                          <div className={`thirty-percent-metric-value ${
                            serverMetrics.riskLevel === 'low' ? 'positive' : serverMetrics.riskLevel === 'moderate' ? '' : 'negative'
                          }`}>
                            {serverMetrics.riskLevel?.toUpperCase()} ({serverMetrics.riskScore}/100)
                          </div>
                          <div className="thirty-percent-metric-detail">
                            {serverMetrics.riskMessage}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (firstModalStrategy && !serverMetrics) {
                  return (
                    <div className="server-metrics-section">
                      <button
                        className="fetch-server-analysis-button"
                        onClick={() => runServerAnalysis(firstModalStrategy, {
                          accountSize,
                          contracts: contractSize,
                          maxDrawdown: riskDrawdown,
                          startOfDayProfit: riskStartOfDayProfit,
                          safetyNet: riskSafetyNet,
                          profitSinceLastPayout: riskProfitSinceLastPayout,
                          contractType: modalContractType,
                        })}
                      >
                        📊 Load Detailed Server Analysis
                      </button>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Why This Matters */}
              <div className="thirty-percent-why-matters">
                <div className="why-matters-title">Why this matters</div>
                <div className="why-matters-content">
                  Large profit days are allowed under Apex rules. However, the larger a strategy's biggest profit day is, the more total profit is required before a payout can be requested.
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Blowout Risk Details Modal */}
      {showBlowoutRiskModal && projectedRisks?.blowoutRiskData?.metrics && ReactDOM.createPortal(
        <div className="performance-explanation-modal-overlay" onClick={() => setShowBlowoutRiskModal(false)}>
          <div className="performance-explanation-modal-content blowout-risk-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="performance-explanation-modal-header">
              <h3>Blowout Risk Analysis</h3>
              <button 
                className="performance-explanation-modal-close"
                onClick={() => setShowBlowoutRiskModal(false)}
              >
                ×
              </button>
            </div>
            <div className="performance-explanation-modal-body">
              {modalServerMetrics?.riskCalculationNote && (
                <div className="thirty-percent-safe-box" style={{ marginBottom: '1rem' }}>
                  <div className="safe-content">
                    <div className="safe-title">How RiskLo Chooses Risk Inputs</div>
                    <div className="safe-message">{modalServerMetrics.riskCalculationNote}</div>
                    {modalServerMetrics.riskCalculationMethod === 'max_historical_vs_sl' && (
                      <div className="safe-message" style={{ marginTop: '0.5rem' }}>
                        Historical loss: ${Number(modalServerMetrics.historicalHighestLoss || 0).toFixed(2)} | SL-based estimate: ${Number(modalServerMetrics.theoreticalStopLoss || 0).toFixed(2)} | Risk basis used: ${Number(modalServerMetrics.riskBasisLoss || modalServerMetrics.highestLoss || 0).toFixed(2)}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {firstModalStrategy && serverAnalysisLoading[firstModalStrategy] && (
                <div className="thirty-percent-safe-box" style={{ marginBottom: '1rem' }}>
                  <div className="safe-content">
                    <div className="safe-title">Loading Enhanced SL Metrics...</div>
                  </div>
                </div>
              )}
              {modalServerMetrics?.hasMultipleTpTargets && modalServerMetrics?.multiTpModelingNote && (
                <div className="thirty-percent-warning-box" style={{ marginBottom: '1rem' }}>
                  <div className="warning-content">
                    <div className="warning-title">Multi-TP Modeling Note</div>
                    <div className="warning-message">{modalServerMetrics.multiTpModelingNote}</div>
                  </div>
                </div>
              )}
              {/* Account Blowout Risk Status */}
              <div className={`blowout-status-card ${projectedRisks.blowoutRisk === 0 && projectedRisks.blowoutRiskData.metrics.buffer >= 0 ? 'safe' : 'warning'}`}>
                <div className="blowout-status-header">
                  <div className="blowout-status-title">
                    Account Blowout Risk
                    {projectedRisks.blowoutRisk === 0 && projectedRisks.blowoutRiskData.metrics.buffer >= 0 ? (
                      <span className="detail-badge safe-badge">SAFE</span>
                    ) : (
                      <span className="detail-badge warning-badge">REQUIRES ATTENTION</span>
                    )}
                  </div>
                  <div className="blowout-status-contract">
                    Contract Type: {modalContractType}
                  </div>
                </div>
                {projectedRisks.blowoutRisk === 0 && projectedRisks.blowoutRiskData.metrics.buffer >= 0 ? (
                  <div className="blowout-status-message safe">
                    <IconCheck size={20} color="#10b981" />
                    <div>
                      <strong>SAFE:</strong> No historical end-of-day losses (with your current contract size of {contractSize}) exceeded your max drawdown. You have ${projectedRisks.blowoutRiskData.metrics.buffer.toFixed(2)} buffer above highest loss.
                    </div>
                  </div>
                ) : (
                  <div className="blowout-status-message warning">
                    <div>
                      <strong>⚠️ RISK DETECTED:</strong> Historical data shows {projectedRisks.blowoutRiskData?.Overall?.breaches || 0} day(s) ({projectedRisks.blowoutRisk.toFixed(1)}%) had end-of-day losses exceeding your max drawdown with {contractSize} contract(s).
                    </div>
                  </div>
                )}
                <div className="blowout-status-probability">
                  Probability of end-of-day losses exceeding max drawdown: <strong>{projectedRisks.blowoutRisk.toFixed(1)}%</strong>
                  {projectedRisks.blowoutRiskData?.Overall?.breaches !== undefined && (
                    <span className="blowout-status-breaches">
                      ({projectedRisks.blowoutRiskData.Overall.breaches} of {projectedRisks.blowoutRiskData.metrics.totalDays} trading days exceeded the limit)
                    </span>
                  )}
                </div>
              </div>

              {/* Overall Risk Score */}
              <div className="blowout-risk-score-section">
                <div className="blowout-risk-score-value">
                  Risk Score: <span className={`risk-score-number ${projectedRisks.blowoutRiskData.metrics.riskScore < 40 ? 'low' : projectedRisks.blowoutRiskData.metrics.riskScore < 70 ? 'moderate' : 'high'}`}>
                    {projectedRisks.blowoutRiskData.metrics.riskScore}/100
                  </span>
                </div>
                <div className={`risk-score-badge ${projectedRisks.blowoutRiskData.metrics.riskScore < 40 ? 'low' : projectedRisks.blowoutRiskData.metrics.riskScore < 70 ? 'moderate' : 'high'}`}>
                  {projectedRisks.blowoutRiskData.metrics.riskScore < 40 ? 'LOW RISK' : 
                   projectedRisks.blowoutRiskData.metrics.riskScore < 70 ? 'MODERATE RISK' : 'HIGH RISK'}
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="blowout-metrics-grid-detailed">
                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">⚠️</div>
                  <div className="blowout-metric-label-detailed">RISK SCORE</div>
                  <div className={`blowout-metric-value-detailed ${projectedRisks.blowoutRiskData.metrics.riskScore < 40 ? 'low' : projectedRisks.blowoutRiskData.metrics.riskScore < 70 ? 'moderate' : 'high'}`}>
                    {projectedRisks.blowoutRiskData.metrics.riskScore}/100
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {projectedRisks.blowoutRiskData.metrics.riskScore < 40 ? 'Low Risk' : 
                     projectedRisks.blowoutRiskData.metrics.riskScore < 70 ? 'Moderate Risk' : 'High Risk'}
                  </div>
                </div>

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">📉</div>
                  <div className="blowout-metric-label-detailed">HIGHEST LOSS</div>
                  <div className="blowout-metric-value-detailed negative">
                    ${projectedRisks.blowoutRiskData.metrics.highestLoss.toFixed(2)}
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {((projectedRisks.blowoutRiskData.metrics.highestLoss / accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                    {modalServerMetrics?.highestLossDate && (
                      <span style={{ display: 'block', marginTop: '0.25rem' }}>Worst: {modalServerMetrics.highestLossDate}</span>
                    )}
                  </div>
                </div>

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">📊</div>
                  <div className="blowout-metric-label-detailed">AVERAGE LOSS</div>
                  <div className="blowout-metric-value-detailed negative">
                    ${projectedRisks.blowoutRiskData.metrics.averageLoss.toFixed(2)}
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {((projectedRisks.blowoutRiskData.metrics.averageLoss / accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">📈</div>
                  <div className="blowout-metric-label-detailed">MAX PROFIT</div>
                  <div className="blowout-metric-value-detailed positive">
                    ${projectedRisks.blowoutRiskData.metrics.maxProfit.toFixed(2)}
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {((projectedRisks.blowoutRiskData.metrics.maxProfit / accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                    {modalServerMetrics?.maxProfitDate && (
                      <span style={{ display: 'block', marginTop: '0.25rem' }}>Best: {modalServerMetrics.maxProfitDate}</span>
                    )}
                  </div>
                </div>
                {modalServerMetrics?.theoreticalStopLoss !== null && modalServerMetrics?.theoreticalStopLoss !== undefined && (
                  <div className="blowout-metric-card-detailed">
                    <div className="blowout-metric-icon-detailed">🛡️</div>
                    <div className="blowout-metric-label-detailed">SL-BASED THEORETICAL LOSS</div>
                    <div className="blowout-metric-value-detailed negative">
                      ${Number(modalServerMetrics.theoreticalStopLoss).toFixed(2)}
                    </div>
                    <div className="blowout-metric-detail-detailed">
                      {modalServerMetrics.theoreticalStopLossPercent || '0.00'}% of account ({modalServerMetrics.strategySlLimitPoints || 0} pts SL)
                    </div>
                  </div>
                )}

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">📈</div>
                  <div className="blowout-metric-label-detailed">AVERAGE PROFIT</div>
                  <div className="blowout-metric-value-detailed positive">
                    ${projectedRisks.blowoutRiskData.metrics.averageProfit.toFixed(2)}
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {((projectedRisks.blowoutRiskData.metrics.averageProfit / accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">👥</div>
                  <div className="blowout-metric-label-detailed">PER-CONTRACT LOSS</div>
                  <div className="blowout-metric-value-detailed negative">
                    ${projectedRisks.blowoutRiskData.metrics.perContractLoss.toFixed(2)}
                  </div>
                  <div className="blowout-metric-detail-detailed">Worst loss per contract</div>
                </div>

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">📅</div>
                  <div className="blowout-metric-label-detailed">TRADING DAYS ANALYZED</div>
                  <div className="blowout-metric-value-detailed">
                    {projectedRisks.blowoutRiskData.metrics.totalDays}
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {projectedRisks.blowoutRiskData.metrics.winningDays} winning, {projectedRisks.blowoutRiskData.metrics.losingDays} losing days
                  </div>
                </div>

                <div className="blowout-metric-card-detailed">
                  <div className="blowout-metric-icon-detailed">🛡️</div>
                  <div className="blowout-metric-label-detailed">MAX DRAWDOWN ANALYSIS</div>
                  <div className="blowout-metric-value-detailed">
                    ${(riskDrawdown || 2500).toLocaleString()}
                  </div>
                  <div className="blowout-metric-detail-detailed">
                    {projectedRisks.blowoutRiskData.metrics.buffer >= 0 
                      ? `No breaches - $${projectedRisks.blowoutRiskData.metrics.buffer.toFixed(2)} buffer`
                      : `Exceeds by $${Math.abs(projectedRisks.blowoutRiskData.metrics.buffer).toFixed(2)}`}
                  </div>
                </div>
              </div>

              {/* Server-side enhanced metrics */}
              {(() => {
                const serverMetrics = modalServerMetrics;
                
                if (serverMetrics && serverMetrics.drawdownBreach) {
                  return (
                    <div className="server-metrics-section">
                      <h4 className="server-metrics-title">Server Analysis (Enhanced)</h4>
                      <div className="blowout-metrics-grid-detailed">
                        <div className="blowout-metric-card-detailed">
                          <div className="blowout-metric-icon-detailed">📊</div>
                          <div className="blowout-metric-label-detailed">BREACH PROBABILITY</div>
                          <div className={`blowout-metric-value-detailed ${Number(serverMetrics.drawdownBreach.breachProbability) === 0 ? 'low' : 'high'}`}>
                            {serverMetrics.drawdownBreach.breachProbability}%
                          </div>
                          <div className="blowout-metric-detail-detailed">
                            {serverMetrics.drawdownBreach.breaches} breaches out of {serverMetrics.totalTradingDays} days
                          </div>
                        </div>
                        <div className="blowout-metric-card-detailed">
                          <div className="blowout-metric-icon-detailed">🛡️</div>
                          <div className="blowout-metric-label-detailed">DRAWDOWN BUFFER</div>
                          <div className={`blowout-metric-value-detailed ${serverMetrics.drawdownBreach.isSafe ? 'low' : 'high'}`}>
                            ${serverMetrics.drawdownBreach.buffer}
                          </div>
                          <div className="blowout-metric-detail-detailed">
                            {serverMetrics.drawdownBreach.isSafe ? 'Within safe limits' : 'Exceeds drawdown limit'}
                          </div>
                        </div>
                        <div className="blowout-metric-card-detailed">
                          <div className="blowout-metric-icon-detailed">📉</div>
                          <div className="blowout-metric-label-detailed">WORST LOSS (SCALED)</div>
                          <div className="blowout-metric-value-detailed negative">
                            ${serverMetrics.worstLossForSize}
                          </div>
                          <div className="blowout-metric-detail-detailed">
                            {serverMetrics.worstLossPercent}% of account
                          </div>
                        </div>
                        <div className="blowout-metric-card-detailed">
                          <div className="blowout-metric-icon-detailed">📊</div>
                          <div className="blowout-metric-label-detailed">AVG LOSS (SCALED)</div>
                          <div className="blowout-metric-value-detailed negative">
                            ${serverMetrics.avgLossForSize}
                          </div>
                          <div className="blowout-metric-detail-detailed">
                            {serverMetrics.avgLossPercent}% of account
                          </div>
                        </div>
                        <div className="blowout-metric-card-detailed">
                          <div className="blowout-metric-icon-detailed">📈</div>
                          <div className="blowout-metric-label-detailed">BEST PROFIT (SCALED)</div>
                          <div className="blowout-metric-value-detailed positive">
                            ${serverMetrics.maxProfitForSize}
                          </div>
                          <div className="blowout-metric-detail-detailed">
                            {contractSize} contract(s) scaled
                          </div>
                        </div>
                        <div className="blowout-metric-card-detailed">
                          <div className="blowout-metric-icon-detailed">💡</div>
                          <div className="blowout-metric-label-detailed">BLOW ACCOUNT STATUS</div>
                          <div className={`blowout-metric-value-detailed ${
                            serverMetrics.blowAccountStatus === 'GO' ? 'low' : 
                            serverMetrics.blowAccountStatus === 'CAUTION' ? 'moderate' : 'high'
                          }`}>
                            {serverMetrics.blowAccountStatus}
                          </div>
                          <div className="blowout-metric-detail-detailed">
                            {serverMetrics.blowAccountMessage}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                if (firstModalStrategy && !serverMetrics) {
                  return (
                    <div className="server-metrics-section">
                      <button
                        className="fetch-server-analysis-button"
                        onClick={() => runServerAnalysis(firstModalStrategy, {
                          accountSize,
                          contracts: contractSize,
                          maxDrawdown: riskDrawdown,
                          startOfDayProfit: riskStartOfDayProfit,
                          safetyNet: riskSafetyNet,
                          profitSinceLastPayout: riskProfitSinceLastPayout,
                          contractType: modalContractType,
                        })}
                      >
                        📊 Load Detailed Server Analysis
                      </button>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Important Warning */}
              <div className="blowout-warning-box-detailed">
                <strong>⚠️ Important:</strong> Trailing drawdown is based on <strong>intraday drawdown movement</strong>, not end-of-day P&amp;L. A trade could close positive but still blow the account if it exceeded the drawdown limit during the day. This analysis uses end-of-day data, so <strong>actual risk may be higher than shown.</strong>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Windfall Details Modal */}
      {showWindfallModal && windfallDetails && ReactDOM.createPortal(
        <div className="performance-explanation-modal-overlay" onClick={() => setShowWindfallModal(false)}>
          <div className="performance-explanation-modal-content windfall-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="performance-explanation-modal-header">
              <h3>Windfall Analysis</h3>
              <button 
                className="performance-explanation-modal-close"
                onClick={() => setShowWindfallModal(false)}
              >
                ×
              </button>
            </div>
            <div className="performance-explanation-modal-body">
              {/* Status Header */}
              {windfallDetails.exceedsThreshold && (
                <div className="windfall-status-banner warning">
                  <span className="status-banner-icon">⚠️</span>
                  <span className="status-banner-text">REQUIRES ATTENTION</span>
                </div>
              )}
              
              {/* Probability Section */}
              <div className="windfall-probability-section">
                <div className="windfall-probability-card">
                  <div className="windfall-probability-label">Probability of exceeding ${windfallDetails.threshold.toFixed(0)} threshold</div>
                  <div className={`windfall-probability-value ${projectedRisks.windfall === 0 ? 'low' : projectedRisks.windfall <= 5 ? 'low' : projectedRisks.windfall <= 10 ? 'moderate' : 'high'}`}>
                    {projectedRisks.windfall.toFixed(2)}%
                  </div>
                  <div className="windfall-probability-detail">
                    ({windfallDetails.breaches} of {windfallDetails.totalDays} historical strategy trading days)
                  </div>
                  <div className={`windfall-risk-badge ${projectedRisks.windfall === 0 ? 'low' : projectedRisks.windfall <= 5 ? 'low' : projectedRisks.windfall <= 10 ? 'moderate' : 'high'}`}>
                    {projectedRisks.windfall === 0 ? 'LOW RISK' : 
                     projectedRisks.windfall <= 5 ? 'LOW RISK' : 
                     projectedRisks.windfall <= 10 ? 'MODERATE RISK' : 'HIGH RISK'}
                  </div>
                  <div className="windfall-probability-note">
                    Probability based on historical strategy performance.
                  </div>
                </div>
              </div>

              {/* Main Metrics Grid */}
              <div className="windfall-details-grid">
                <div className={`windfall-detail-card ${windfallDetails.isAligned ? 'safe' : ''}`}>
                  <div className="windfall-detail-label">
                    Largest Safe Strategy Profit Day for Fastest Payout
                    {windfallDetails.isAligned && <span className="detail-badge safe-badge">ALIGNED</span>}
                  </div>
                  <div className="windfall-detail-value positive">
                    ${windfallDetails.largestSafeDayForFastestPayout.toFixed(2)}
                  </div>
                  <div className="windfall-detail-subtext">
                    Fastest payout path (payout at $2,600 is achievable)
                  </div>
                </div>
                
                <div className="windfall-detail-card">
                  <div className="windfall-detail-label">Strategy's Highest Historical Profit Day</div>
                  <div className={`windfall-detail-value ${windfallDetails.exceedsThreshold ? 'negative' : 'positive'}`}>
                    ${windfallDetails.highestProfit.toFixed(2)}
                  </div>
                  <div className="windfall-detail-subtext">
                    {((windfallDetails.highestProfit / windfallDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>
              </div>

              {/* Additional Metrics Grid */}
              <div className="windfall-metrics-grid">
                <div className="windfall-metric-card">
                  <div className="windfall-metric-icon">📈</div>
                  <div className="windfall-metric-label">MAXIMUM PROFIT</div>
                  <div className="windfall-metric-value positive">
                    ${windfallDetails.maxProfit.toFixed(2)}
                  </div>
                  <div className="windfall-metric-detail">
                    {((windfallDetails.maxProfit / windfallDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>

                <div className="windfall-metric-card">
                  <div className="windfall-metric-icon">📈</div>
                  <div className="windfall-metric-label">AVERAGE PROFIT</div>
                  <div className="windfall-metric-value positive">
                    ${windfallDetails.averageProfit.toFixed(2)}
                  </div>
                  <div className="windfall-metric-detail">
                    {((windfallDetails.averageProfit / windfallDetails.accountSize) * 100).toFixed(2)}% of account ({contractSize} {modalContractType === 'MNQ' ? 'MNQ' : 'NQ'})
                  </div>
                </div>

                <div className="windfall-metric-card">
                  <div className="windfall-metric-icon">📅</div>
                  <div className="windfall-metric-label">TRADING DAYS ANALYZED</div>
                  <div className="windfall-metric-value">
                    {windfallDetails.totalDays}
                  </div>
                  <div className="windfall-metric-detail">
                    {windfallDetails.winningDays} winning, {windfallDetails.losingDays} losing days
                  </div>
                </div>

                <div className="windfall-metric-card">
                  <div className="windfall-metric-icon">💰</div>
                  <div className="windfall-metric-label">WINDFALL THRESHOLD</div>
                  <div className="windfall-metric-value">
                    ${windfallDetails.threshold.toFixed(2)}
                  </div>
                  <div className="windfall-metric-detail">
                    {riskConsistencyPercent}% of ${windfallDetails.profitSinceLastPayout.toFixed(2)} profit since last payout
                  </div>
                </div>
              </div>

              {/* Why This Matters */}
              <div className="windfall-why-matters">
                <div className="why-matters-title">Why this matters</div>
                <div className="why-matters-content">
                  Large profit days are allowed under Apex rules. However, the larger a strategy's biggest profit day is, the more total profit is required before a payout can be requested.
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Performance Explanation Modal */}
      {showExplanationModal && explanationData && ReactDOM.createPortal(
        <div className="performance-explanation-modal-overlay" onClick={() => setShowExplanationModal(false)}>
          <div className="performance-explanation-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="performance-explanation-modal-header">
              <h3>{explanationData.title}</h3>
              <button 
                className="performance-explanation-modal-close"
                onClick={() => setShowExplanationModal(false)}
              >
                ×
              </button>
            </div>
            <div className="performance-explanation-modal-body">
              <div className="explanation-value-display">
                <span className="explanation-label">Result:</span>
                <span className={`explanation-value ${explanationData.type === 'winRate' ? (explanationData.value.includes('%') && parseFloat(explanationData.value) >= 50 ? 'positive' : 'negative') : (explanationData.type === 'profitFactor' ? (explanationData.value === PROFIT_FACTOR_NO_LOSSES_LABEL || parseFloat(explanationData.value) >= 1 ? 'positive' : 'negative') : (explanationData.value.includes('$') && parseFloat(explanationData.value.replace(/[^0-9.-]/g, '')) >= 0 ? 'positive' : 'negative'))}`}>
                  {explanationData.value}
                </span>
              </div>
              
              <div className="explanation-section">
                <h4>Formula</h4>
                <p className="explanation-formula">{explanationData.explanation}</p>
              </div>

              <div className="explanation-section">
                <h4>Calculation</h4>
                <p className="explanation-calculation">{explanationData.calculation}</p>
              </div>

              {explanationData.percentageExplanation && (
                <>
                  <div className="explanation-section">
                    <h4>Percentage Formula</h4>
                    <p className="explanation-formula">{explanationData.percentageExplanation}</p>
                  </div>

                  <div className="explanation-section">
                    <h4>Percentage Calculation</h4>
                    <p className="explanation-calculation">{explanationData.percentageCalculation}</p>
                  </div>
                </>
              )}

              {explanationData.details && (
                <div className="explanation-section">
                  <h4>Interpretation</h4>
                  <p className="explanation-details">{explanationData.details}</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {sourceTabs.length > 1 && (
        <div className="portfolio-sim-source-tabs" role="tablist" aria-label="What to show">
          {sourceTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              className={`portfolio-sim-source-tab${activeTab === t.id ? ' active' : ''}`}
              onClick={() => setSourceTab(t.id)}
            >
              {t.label}
              <span className="portfolio-sim-source-tab-count">{t.count}</span>
            </button>
          ))}
        </div>
      )}

      {mixSet.size > 0 && (
        <div className="portfolio-sim-mix-bar" role="region" aria-label="Your mix">
          <span className="portfolio-sim-mix-label">Mix ({mixSet.size}):</span>
          <div className="portfolio-sim-mix-chips">
            {Array.from(mixSet).map((name) => (
              <span key={name} className={`portfolio-sim-mix-chip is-${groupOf(name)}`}>
                {resolveStrategyDisplayName(name)}
                <button type="button" aria-label={`Remove ${name} from the mix`} onClick={() => toggleMix(name)}>×</button>
              </span>
            ))}
          </div>
          <div className="portfolio-sim-mix-actions">
            <button
              type="button"
              className="portfolio-sim-mix-go"
              onClick={() => {
                openSimulatorFor(Array.from(mixSet));
                trackEvent('portfolio_mix_opened', { size: mixSet.size, groups: [...new Set(Array.from(mixSet).map(groupOf))].join(',') });
              }}
            >
              {mixSet.size === 1 ? 'Simulate' : 'Simulate together'}
            </button>
            <button type="button" className="portfolio-sim-mix-clear" onClick={() => setMixSet(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {loading && strategies.length === 0 ? (
        <div className="portfolio-simulator-loading">
          <p>Loading strategy data...</p>
        </div>
      ) : (
        <div className="portfolio-simulator-table-wrapper">
          <table className="portfolio-simulator-table">
            <thead>
              <tr>
                <th className="mix-cell" title="Tick to add to your mix">Mix</th>
                <th>Rank</th>
                <th 
                  className={`sortable ${sortBy === 'strategy' ? 'active' : ''}`}
                  onClick={() => handleSort('strategy')}
                >
                  {nameColumnLabel}
                  {sortBy === 'strategy' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
                <th 
                  className={`sortable ${sortBy === 'monday' ? 'active' : ''}`}
                  onClick={() => handleSort('monday')}
                >
                  {getCurrentStatLabel()} Mon
                  {sortBy === 'monday' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
                <th 
                  className={`sortable ${sortBy === 'tuesday' ? 'active' : ''}`}
                  onClick={() => handleSort('tuesday')}
                >
                  {getCurrentStatLabel()} Tue
                  {sortBy === 'tuesday' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
                <th 
                  className={`sortable ${sortBy === 'wednesday' ? 'active' : ''}`}
                  onClick={() => handleSort('wednesday')}
                >
                  {getCurrentStatLabel()} Wed
                  {sortBy === 'wednesday' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
                <th 
                  className={`sortable ${sortBy === 'thursday' ? 'active' : ''}`}
                  onClick={() => handleSort('thursday')}
                >
                  {getCurrentStatLabel()} Thur
                  {sortBy === 'thursday' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
                <th 
                  className={`sortable ${sortBy === 'friday' ? 'active' : ''}`}
                  onClick={() => handleSort('friday')}
                >
                  {getCurrentStatLabel()} Fri
                  {sortBy === 'friday' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
                <th 
                  className={`sortable ${sortBy === 'overall' ? 'active' : ''}`}
                  onClick={() => handleSort('overall')}
                >
                  {getCurrentStatLabel()} Overall
                  {sortBy === 'overall' && (sortOrder === 'desc' ? ' ↓' : ' ↑')}
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleStrategies.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">
                    {strategies.length === 0 
                      ? 'No strategy data available. Click "Run Analysis" to load data.'
                      : 'No strategies match the current filter.'}
                  </td>
                </tr>
              ) : (
                visibleStrategies.map((strategy) => {
                  const statFormat = STATISTICS_OPTIONS.find(opt => opt.value === selectedStatistic)?.format || 'number';
                  const statValue = getStatValue(strategy, 'Monday');
                  const showDash = (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') && 
                                   !riskCalculated && 
                                   (statValue === null || statValue === undefined);
                  const strategyLoadState = strategyLoadingStates[strategy.strategy];
                  const isStrategyLoading = strategyLoadState === 'loading';
                  const pfNoLossesTitle = (dayKey) =>
                    !showDash && selectedStatistic === 'pf' && getStatValue(strategy, dayKey) === Infinity
                      ? PROFIT_FACTOR_NO_LOSSES_TITLE
                      : undefined;

                  const isInternalStrategy = internalStrategySet.has(
                    String(strategy.strategy || '').trim().toLowerCase(),
                  );

                  return (
                    <tr
                      key={strategy.strategy}
                      className={`${isStrategyLoading ? 'strategy-loading' : ''}${isInternalStrategy ? ' is-internal' : ''}`}
                    >
                      <td className="mix-cell">
                        <input
                          type="checkbox"
                          checked={mixSet.has(strategy.strategy)}
                          onChange={() => toggleMix(strategy.strategy)}
                          aria-label={`Add ${resolveStrategyDisplayName(strategy.strategy)} to the mix`}
                        />
                      </td>
                      <td className="rank-cell">{strategy.rank}</td>
                      <td className="strategy-name-cell">
                        <span
                          className="strategy-name-link"
                          onClick={() => openSimulatorForStrategy(strategy.strategy)}
                        >
                          {/* Show the admin's rename, not the raw data name the
                              row is keyed and fetched by. */}
                          {resolveStrategyDisplayName(strategy.strategy)}
                        </span>
                        {/* Only ever rendered for admins: members never receive
                            internal strategies in the first place. */}
                        {isInternalStrategy ? (
                          <span className="simulator-internal-badge" title="Internal — admins only">
                            Internal
                          </span>
                        ) : null}
                        {activeTab === 'all' && strategy.group === 'portfolio' ? (
                          <span className="simulator-source-badge is-portfolio" title="One of your connected accounts">Account</span>
                        ) : null}
                        {activeTab === 'all' && strategy.group === 'mine' ? (
                          <span className="simulator-source-badge is-mine" title="Your own strategy (only you can see it)">My strategy</span>
                        ) : null}
                      </td>
                      <td 
                        className={`stat-cell ${showDash ? '' : getValueColorClass(getStatValue(strategy, 'Monday'), selectedStatistic)}`}
                        title={pfNoLossesTitle('Monday')}
                        onMouseEnter={(e) => handleCellHover(e, strategy, 'Monday')}
                        onMouseLeave={handleCellLeave}
                      >
                        {showDash ? '-' : formatValue(getStatValue(strategy, 'Monday'), statFormat, selectedStatistic)}
                      </td>
                      <td 
                        className={`stat-cell ${showDash ? '' : getValueColorClass(getStatValue(strategy, 'Tuesday'), selectedStatistic)}`}
                        title={pfNoLossesTitle('Tuesday')}
                        onMouseEnter={(e) => handleCellHover(e, strategy, 'Tuesday')}
                        onMouseLeave={handleCellLeave}
                      >
                        {showDash ? '-' : formatValue(getStatValue(strategy, 'Tuesday'), statFormat, selectedStatistic)}
                      </td>
                      <td 
                        className={`stat-cell ${showDash ? '' : getValueColorClass(getStatValue(strategy, 'Wednesday'), selectedStatistic)}`}
                        title={pfNoLossesTitle('Wednesday')}
                        onMouseEnter={(e) => handleCellHover(e, strategy, 'Wednesday')}
                        onMouseLeave={handleCellLeave}
                      >
                        {showDash ? '-' : formatValue(getStatValue(strategy, 'Wednesday'), statFormat, selectedStatistic)}
                      </td>
                      <td 
                        className={`stat-cell ${showDash ? '' : getValueColorClass(getStatValue(strategy, 'Thursday'), selectedStatistic)}`}
                        title={pfNoLossesTitle('Thursday')}
                        onMouseEnter={(e) => handleCellHover(e, strategy, 'Thursday')}
                        onMouseLeave={handleCellLeave}
                      >
                        {showDash ? '-' : formatValue(getStatValue(strategy, 'Thursday'), statFormat, selectedStatistic)}
                      </td>
                      <td 
                        className={`stat-cell ${showDash ? '' : getValueColorClass(getStatValue(strategy, 'Friday'), selectedStatistic)}`}
                        title={pfNoLossesTitle('Friday')}
                        onMouseEnter={(e) => handleCellHover(e, strategy, 'Friday')}
                        onMouseLeave={handleCellLeave}
                      >
                        {showDash ? '-' : formatValue(getStatValue(strategy, 'Friday'), statFormat, selectedStatistic)}
                      </td>
                      <td 
                        className={`stat-cell stat-overall ${showDash ? '' : getValueColorClass(getStatValue(strategy, 'Overall'), selectedStatistic)}`}
                        title={pfNoLossesTitle('Overall')}
                        onMouseEnter={(e) => handleCellHover(e, strategy, 'Overall')}
                        onMouseLeave={handleCellLeave}
                      >
                        {showDash ? '-' : formatValue(getStatValue(strategy, 'Overall'), statFormat, selectedStatistic)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {processedStrategies.length > 0 && (
        <div className="portfolio-simulator-footer">
          <div className="footer-info">
            <p className="footer-hint">Click column headers to sort • Click a name to simulate it, or tick several (across tabs) and Simulate together • Hover over values to see all statistics • Results auto-update on input change</p>
          </div>
          {riskCalculated && (selectedStatistic === 'blowoutRisk' || selectedStatistic === 'thirtyPercentRule') && (
            <div className="footer-actions">
              <button
                className="footer-action-button"
                onClick={() => {
                  const riskType = selectedStatistic === 'blowoutRisk' ? 'blowoutRisk' : 'thirtyPercentRule';
                  const headers = ['Rank', 'Strategy', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Overall'];
                  const rows = processedStrategies.map(s => {
                    const riskData = riskCalculations[s.strategy];
                    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Overall'];
                    const values = days.map(d => {
                      const dayRisk = riskData?.[riskType]?.[d];
                      return dayRisk ? `${dayRisk.probability.toFixed(1)}%` : '-';
                    });
                    return [s.rank, s.strategy, ...values].join('\t');
                  });
                  const text = [headers.join('\t'), ...rows].join('\n');
                  navigator.clipboard.writeText(text).then(() => {
                    setCopyResultsFeedback(true);
                    setTimeout(() => setCopyResultsFeedback(false), 1500);
                  });
                }}
                title="Copy results to clipboard"
              >
                <IconCopy size={16} style={{ marginRight: '0.35rem', verticalAlign: 'middle' }} />
                <span className="copy-results-btn">{copyResultsFeedback ? 'Copied!' : 'Copy to Clipboard'}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Statistics Tooltip */}
      {hoveredCell && (
        <div 
          className="stat-tooltip"
          style={{
            position: 'fixed',
            left: `${hoveredCell.x}px`,
            top: `${hoveredCell.y}px`,
            transform: 'translate(-50%, -100%)',
            pointerEvents: 'none',
            zIndex: 10000
          }}
        >
          <div className="stat-tooltip-header">
            <strong>{hoveredCell.strategy}</strong>
            <span className="stat-tooltip-day">{hoveredCell.day}</span>
          </div>
          <div className="stat-tooltip-content">
            {hoveredCell.isRisk ? (
              // Risk statistics tooltip (Blowout Risk or 30% Rule)
              <>
                {hoveredCell.riskType === 'blowoutRisk' && (
                  <>
                    <div className="stat-tooltip-row">
                      <span className="stat-tooltip-label">Blowout Risk:</span>
                      <span className={`stat-tooltip-value ${hoveredCell.riskData.probability <= 5 ? 'positive' : hoveredCell.riskData.probability <= 10 ? 'caution' : 'negative'}`}>
                        {hoveredCell.riskData.probability.toFixed(1)}%
                      </span>
                    </div>
                    <div className="stat-tooltip-row">
                      <span className="stat-tooltip-label">Breaches:</span>
                      <span className="stat-tooltip-value">{hoveredCell.riskData.breaches}</span>
                    </div>
                    <div className="stat-tooltip-row">
                      <span className="stat-tooltip-label">Trading Days:</span>
                      <span className="stat-tooltip-value">{hoveredCell.riskData.totalDays}</span>
                    </div>
                    {hoveredCell.metrics && (
                      <>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Highest Loss:</span>
                          <span className="stat-tooltip-value negative">
                            ${hoveredCell.metrics.highestLoss.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Average Loss:</span>
                          <span className="stat-tooltip-value negative">
                            ${hoveredCell.metrics.averageLoss.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Max Profit:</span>
                          <span className="stat-tooltip-value positive">
                            ${hoveredCell.metrics.maxProfit.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Risk Score:</span>
                          <span className={`stat-tooltip-value ${hoveredCell.metrics.riskScore < 40 ? 'positive' : hoveredCell.metrics.riskScore < 70 ? 'caution' : 'negative'}`}>
                            {hoveredCell.metrics.riskScore}/100
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
                {hoveredCell.riskType === 'thirtyPercentRule' && (
                  <>
                    <div className="stat-tooltip-row">
                      <span className="stat-tooltip-label">% Rule Risk:</span>
                      <span className={`stat-tooltip-value ${hoveredCell.riskData.probability <= 5 ? 'positive' : hoveredCell.riskData.probability <= 10 ? 'caution' : 'negative'}`}>
                        {hoveredCell.riskData.probability.toFixed(1)}%
                      </span>
                    </div>
                    <div className="stat-tooltip-row">
                      <span className="stat-tooltip-label">Breaches:</span>
                      <span className="stat-tooltip-value">{hoveredCell.riskData.breaches}</span>
                    </div>
                    <div className="stat-tooltip-row">
                      <span className="stat-tooltip-label">Trading Days:</span>
                      <span className="stat-tooltip-value">{hoveredCell.riskData.totalDays}</span>
                    </div>
                    {hoveredCell.riskData.maeLimit && (
                      <div className="stat-tooltip-row">
                        <span className="stat-tooltip-label">Consistency Limit:</span>
                        <span className="stat-tooltip-value">
                          ${hoveredCell.riskData.maeLimit.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {hoveredCell.thirtyPercentMetrics && (
                      <>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Worst Loss:</span>
                          <span className="stat-tooltip-value negative">
                            ${hoveredCell.thirtyPercentMetrics.worstLoss.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Highest Profit:</span>
                          <span className="stat-tooltip-value positive">
                            ${hoveredCell.thirtyPercentMetrics.highestProfit.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Average Loss:</span>
                          <span className="stat-tooltip-value negative">
                            ${hoveredCell.thirtyPercentMetrics.averageLoss.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Average Profit:</span>
                          <span className="stat-tooltip-value positive">
                            ${hoveredCell.thirtyPercentMetrics.averageProfit.toFixed(2)}
                          </span>
                        </div>
                        <div className="stat-tooltip-row">
                          <span className="stat-tooltip-label">Per-Contract Loss:</span>
                          <span className="stat-tooltip-value negative">
                            ${hoveredCell.thirtyPercentMetrics.perContractLoss.toFixed(2)}
                          </span>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              // Regular statistics tooltip (apply contract size multiplier to avgWin, avgLoss, netPL)
              hoveredCell.stats && (() => {
                const mult = Math.max(1, parseInt(simContractSize, 10) || 1);
                return (
                <>
                  <div className="stat-tooltip-row">
                    <span className="stat-tooltip-label">Win Rate:</span>
                    <span className={`stat-tooltip-value ${(hoveredCell.stats.winRate || 0) > 50 ? 'positive' : ''}`}>
                      {formatValue(hoveredCell.stats.winRate, 'percent')}
                    </span>
                  </div>
                  <div className="stat-tooltip-row">
                    <span className="stat-tooltip-label">Avg Win:</span>
                    <span className="stat-tooltip-value positive">{formatValue((hoveredCell.stats.avgWin ?? 0) * mult, 'currency')}</span>
                  </div>
                  <div className="stat-tooltip-row">
                    <span className="stat-tooltip-label">Avg Loss:</span>
                    <span className="stat-tooltip-value negative">{formatValue((hoveredCell.stats.avgLoss ?? 0) * mult, 'currency')}</span>
                  </div>
                  <div className="stat-tooltip-row">
                    <span className="stat-tooltip-label">Net P&L:</span>
                    <span className={`stat-tooltip-value ${(hoveredCell.stats.netPL ?? 0) * mult >= 0 ? 'positive' : 'negative'}`}>
                      {formatValue((hoveredCell.stats.netPL ?? 0) * mult, 'currency')}
                    </span>
                  </div>
                  <div className="stat-tooltip-row">
                    <span className="stat-tooltip-label">Total Trades:</span>
                    <span className="stat-tooltip-value">{Math.round(hoveredCell.stats.totalTrades)}</span>
                  </div>
                  <div className="stat-tooltip-row">
                    <span className="stat-tooltip-label">Profit Factor:</span>
                    <span className={`stat-tooltip-value ${(hoveredCell.stats.pf ?? 0) >= 1 ? 'positive' : 'negative'}`}>
                      {formatValue(hoveredCell.stats.pf, 'number', 'pf')}
                    </span>
                  </div>
                </>
                );
              })()
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PortfolioSimulator;
